import moment from "moment";
import { newCommonFormatCurrency } from "../services/mathServices";
import InvoiceThemeService from "./invoiceThemeService";
import { InvoiceServices } from "../invoice/invoiceServices";
import { sanitize, sanitizeCssClass, sanitizeInlineStyle } from "../../helpers/sanitizer";

// From ./locals/fr.json (invoice.paymentDetails)
const DEFAULT_INVOICE_PAYMENT_DETAILS =
    "Le règlement anticipé ne donne pas lieu à escompte. En cas de règlement tardif, il sera appliqué un intérêt égal à 3 fois le taux légal. Outre des pénalités de retard déjà prévues par la loi, une indemnité forfaitaire pour frais de recouvrement de 40 euros sera appliquée.";

export default class InvoiceHtmlBuilder {
    static ptToMm = 0.36; // 0.352778
    static lineHeight = 1.5;
    static emitterReceiverNamesFontEM = 1.3; // Font size em value eg. 1.3em
    static normalTextFontEM = 1; // 1em
    static tableCellPaddingMm = 1.5; //1.5mm
    static tableHeaderPaddingMm = 2; //1.5mm
    static vatAndTotalsBoxTopMarginMm = 5; //5mm
    static paymentDetailsTextFontEM = 0.8; // 0.8em
    static tableDescriptionTextFontEM = 0.7; // 0.7em
    static defaultBorderWidthInMm = 0.15; // 0.15mm

    readonly forPrint: boolean;
    private invoiceThemeSettings_: any;
    readonly invoiceData: any; // Do not use this attribute directly. Instead use the getter processedInvoiceData
    readonly backgroundImage?: string;

    readonly quotation: any = null; // Contains the parent invoice data in case of a deposit. Same structure as invoiceData
    readonly company: any;
    readonly scaleSize: number = 1;
    readonly showBoxShadow: boolean = false;
    readonly editingClass: string | null | undefined;
    readonly invoiceService: InvoiceServices;

    private computation;

    private localization = "fr-FR"; //Eg. auto, fr, fr-FR, en, en-US . auto to use local settings

    private domInfo: any;

    public internalNPages = 0;

    public get nPages() {
        return this.internalNPages;
    }

    constructor(
        forPrint: boolean = false,
        invoiceThemeSettings: any,
        invoiceData: any,
        quotation,
        company,
        scaleSize,
        showBoxShadow,
        backgroundImage = null,
        editingClass = null,
        domInfo = null
    ) {
        // Make copies of objects to avoid mutating original objects
        this.forPrint = forPrint;
        this.invoiceData = JSON.parse(JSON.stringify(invoiceData));
        this.invoiceThemeSettings_ = JSON.parse(
            JSON.stringify(invoiceThemeSettings ?? InvoiceThemeService.originalTheme())
        );
        this.backgroundImage = backgroundImage;
        this.quotation = quotation ? JSON.parse(JSON.stringify(quotation)) : quotation;
        this.company = company ? JSON.parse(JSON.stringify(company)) : company;
        this.scaleSize = scaleSize;
        this.showBoxShadow = showBoxShadow;
        this.backgroundImage = backgroundImage;
        this.editingClass = editingClass;
        this.domInfo = domInfo ? JSON.parse(JSON.stringify(domInfo)) : domInfo;

        const lSign = this.invoiceData?.type !== "CREDIT_NOTE" ? 1 : -1;
        const vatType = this.invoiceData?.vatType || "NO_VAT_TYPE";
        const defaultSalesAccountableAccount = "700000";

        // const creditNote = invoice.type === "CREDIT_NOTE"

        // const lSign = creditNote ? -sign : sign

        this.invoiceService = new InvoiceServices(
            this.invoiceData?.lines,
            lSign,
            vatType,
            this.invoiceData?.discount,
            this.invoiceData?.discountPercentage,
            defaultSalesAccountableAccount,
            this.invoiceData?.depositOf ? this.quotation : undefined
        );
        this.computation = this.invoiceService.computation;

        // console.log("Raw invoiceData", invoiceData, "this.domInfo", this.domInfo, +new Date(), "this.invoiceThemeSettings", this.invoiceThemeSettings)
        // console.log("Company", company)
        // const diff = {}
        // InvoiceThemeService.objDiff(this.invoiceThemeSettings, InvoiceThemeService.getDefaultInvoiceThemeSettings(), diff)
        // console.log("diff", diff)
    }

    get invoiceThemeSettings() {
        if (this.invoiceThemeSettings_) {
            return JSON.parse(JSON.stringify(this.invoiceThemeSettings_));
        }
        return this.invoiceThemeSettings_;
    }

    get pageHeightInMM() {
        return 297;
    }

    get pageWidthInMM() {
        return 210;
    }

    public setDomInfo(newDomInfo) {
        if (!newDomInfo) {
            throw Error("Value error");
        }
        this.domInfo = newDomInfo;
    }

    protected buildHead() {
        return `
            <head>
                <meta charset="utf-8" />
                <meta http-equiv="X-UA-Compatible" content="IE=edge">
                <meta name="viewport" content="width=device-width, initial-scale=1">
                ${
                    this.forPrint
                        ? `<link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css" rel="stylesheet" integrity="sha384-QWTKZyjpPEjISv5WaRU9OFeRpok6YctnYmDr5pNlyT2bRjXh0JMhjY6hW+ALEwIH" crossorigin="anonymous">`
                        : ""
                }
                <style>
                    @import url('https://fonts.googleapis.com/css2?family=Mulish:ital,wght@0,200..1000;1,200..1000&display=swap');
                    @import url('https://fonts.googleapis.com/css2?family=Mulish:ital,wght@0,200..1000;1,200..1000&family=Roboto:ital,wght@0,100;0,300;0,400;0,500;0,700;0,900;1,100;1,300;1,400;1,500;1,700;1,900&display=swap');
                    @import url('https://fonts.googleapis.com/css2?family=Courier+Prime:ital,wght@0,400;0,700;1,400;1,700&display=swap');

                    ${this.bootstrapStyles}

                    @page {
                        size: A4;
                        margin: 0;
                    }
                    body#previewBody{
                        padding: 0 !important;

                        font-family: ${this.fontFamilyValue};
                        font-size: ${this.fontSizeValue()};
                    }

                    #previewWrapper {
                        box-sizing: border-box;

                        /* Now handled in .page */
                        width: 210mm !important;
                        min-height: 297mm !important;
                        /*height: 297mm !important;*/


                        font-family: ${this.fontFamilyValue};
                        font-size: ${this.fontSizeValue()};
                        line-height: 1.5;
                        color: #010b13;

                        transform: scale(${this.forPrint ? 1 : this.scaleSize}) !important;
                        transform-origin: top left !important;

                    }

                    #innerWrapper {
                        box-sizing: border-box;
                    }




                    #previewWrapper #innerWrapper .page {
                        break-after: always;

                        width: 210mm !important;
                        height: 297mm !important;

                        position: relative;

                        transform: translateZ(0);  /* The element will act as a viewport now and static positionning inside will be relative to it */

                        padding: ${this.cssPagePaddingValue};


                        ${
                            !this.forPrint && this.showBoxShadow
                                ? "box-shadow: 0 0 5px !important;"
                                : ""
                        }

                        overflow-y: hidden !important;
                        overflow-x: hidden !important;

                        margin-left: 0 !important;
                        margin-right: 0 !important;

                        ${!this.forPrint ? `margin-bottom: 3mm;` : ""}
                    }

                    #previewWrapper #innerWrapper .page:before {
                        content: "";
                        ${
                            /*this.invoiceThemeSettings?.page?.useBackgroundImage && selectedDefaultBackgroundKey ? 'background-image: url("' + selectedDefaultBackgroundKey + '");' : ''*/ ""
                        }
                        ${
                            this.invoiceThemeSettings?.page?.useBackgroundImage &&
                            this.backgroundImage /*&& !selectedDefaultBackgroundKey*/
                                ? 'background-image: url("' + this.backgroundImage + '");'
                                : ""
                        }
                        ${
                            !this.invoiceThemeSettings?.page?.useBackgroundImage ||
                            !this.backgroundImage
                                ? "background-color: white;"
                                : ""
                        }  /* TODO handle background colors */
                        ${
                            /*!this.invoiceThemeSettings?.page?.useBackgroundImage || (!selectedDefaultBackgroundKey && !this.backgroundImage) ? 'background-color: white;' : ''*/ ""
                        }  /* TODO handle background colors */

                        background-size: ${
                            this.invoiceThemeSettings?.page?.backgroundSize || "cover"
                        };
                        background-position: ${
                            this.invoiceThemeSettings?.page?.backgroundPosition || "center"
                        };
                        background-repeat: ${
                            this.invoiceThemeSettings?.page?.backgroundRepeat || "repeat"
                        };

                        display: block;
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        height: 100%;
                        opacity: ${
                            this.invoiceThemeSettings?.page?.useBackgroundImage
                                ? this.invoiceThemeSettings?.page?.backgroundOpacity ?? 100
                                : 100
                        }%;
                        z-index: -10;

                    }

                    #previewWrapper #innerWrapper .page.full {
                        height: auto !important;
                    }

                    #previewWrapper table.mainTable td{
                        ${this.generateTableCellStyle(null, true)}
                    }

                    #previewWrapper table.mainTable td:first-child {
                        border-left: none;
                    }

                    #previewWrapper table.mainTable td:last-child {
                        border-right: none;
                    }

                    #previewWrapper table.mainTable tr:first-child td {
                        border-top: none;
                    }

                    #previewWrapper table.mainTable tr:last-child td {
                        border-bottom: none;
                    }

                    #previewWrapper table.mainTable th {
                        ${this.generateTableHeaderStyle(null, true)}
                        border-top: none;
                    }

                    #previewWrapper table.mainTable th:first-child {
                        border-left: none;
                    }

                    #previewWrapper table.mainTable th:last-child {
                        border-right: none;
                    }


                    #previewWrapper table.vatTable td{
                        ${this.generateVATTableCellStyle(null, true)}
                    }

                    #previewWrapper table.vatTable td:first-child {
                        border-left: none;
                    }

                    #previewWrapper table.vatTable td:last-child {
                        border-right: none;
                    }

                    #previewWrapper table.vatTable tr:first-child td {
                        border-top: none;
                    }

                    #previewWrapper table.vatTable tr:last-child td {
                        border-bottom: none;
                    }

                    #previewWrapper table.vatTable th {
                        ${this.generateVATTableHeaderStyle(null, true)}
                        border-top: none;
                    }

                    #previewWrapper table.vatTable th:first-child {
                        border-left: none;
                    }

                    #previewWrapper table.vatTable th:last-child {
                        border-right: none;
                    }


                    @page {
                        size: A4;
                    }



                    /*.background-image {
                        ${
                            this.forPrint
                                ? `position: fixed;
                            top: -${
                                this.cssPageVerticalPaddingValue
                            }mm; /* Adjust according to margin */
                            left: -${
                                this.cssPageHorizontalPaddingValue
                            }mm; /* Adjust according to margin */
                            right: -${
                                this.cssPageHorizontalPaddingValue
                            }mm; /* Adjust according to margin */
                            bottom: -${
                                this.cssPageVerticalPaddingValue
                            }mm; /* Adjust according to margin */
                            width: calc(100% + ${
                                this.extraWidthForBackground
                            }); /* Adjust according to margin */
                            height: calc(100% + ${
                                this.extraHeightForBackground
                            }); /* Adjust according to margin */
                            z-index: -1;

                            ${
                                this.invoiceThemeSettings?.page?.useBackgroundImage &&
                                this.backgroundImage /*&& !selectedDefaultBackgroundKey*/
                                    ? 'background-image: url("' + this.backgroundImage + '");'
                                    : ""
                            }
                            ${
                                !this.invoiceThemeSettings?.page?.useBackgroundImage ||
                                !this.backgroundImage
                                    ? "background-color: white;"
                                    : ""
                            }  /* TODO handle background colors */
                            ${
                                /*!this.invoiceThemeSettings?.page?.useBackgroundImage || (!selectedDefaultBackgroundKey && !this.backgroundImage) ? 'background-color: white;' : ''*/ ""
                            }  /* TODO handle background colors */

                            background-size: ${
                                this.invoiceThemeSettings?.page?.backgroundSize || "cover"
                            };
                            background-position: ${
                                this.invoiceThemeSettings?.page?.backgroundPosition || "center"
                            };
                            background-repeat: ${
                                this.invoiceThemeSettings?.page?.backgroundRepeat || "repeat"
                            };`
                                : ""
                        }

                        ${!this.forPrint ? "display: none; height: 0;" : ""}
                    }*/


                    @media print {
                        @page {
                            size: A4;

                            /*margin: ${this.cssPagePaddingValue}*/

                        }

                        body#previewBody {
                            padding: 0 !important;
                        }

                        #previewWrapper {
                            transform: scale(1) !important;
                            transform-origin: top left !important;

                            page-break-inside: avoid;
                        }

                        .page-footer {
                            position: fixed !important;
                            bottom: 0 !important;
                            left: 0 !important;
                            /* background-color: white; */
                            z-index: 1000;
                        }
                    }

                    /*html  {*/
                    /*body#previewBody {*/
                        /*border: 2px solid red;*/

                        /*transform: scale(${this.forPrint ? 1 : this.scaleSize}) !important;*/
                        /*transform-origin: top left !important;*/
                    /*}*/

                    #previewWrapper .v-divider {
                        display: block;
                        -webkit-box-flex: 1;
                        -ms-flex: 1 1 0px;
                        flex: 1 1 0px;
                        max-width: 100%;
                        height: 0px;
                        max-height: 0px;
                        border: solid;
                        border-width: thin 0 0 0;
                        -webkit-transition: inherit;
                        transition: inherit;
                    }
                    #previewWrapper .v-responsive {
                        position: relative;
                        overflow: hidden;
                        -webkit-box-flex: 1;
                        -ms-flex: 1 0 auto;
                        flex: 1 0 auto;
                        max-width: 100%;
                        display: -webkit-box;
                        display: -ms-flexbox;
                        display: flex;
                    }

                    #previewWrapper .container, #previewWrapper.container, #previewWrapper .container-sm, #previewWrapper.container-sm {
                        max-width: none;
                    }

                    #previewWrapper td {
                        box-sizing: border-box;
                    }

                    #previewWrapper thead {
                        font-weight: bold;
                    }

                    #previewWrapper .page-footer {
                        /*min-height: 50px !important; */
                        /*height: 50px;*/
                        position: fixed !important;
                        bottom: 0 !important;
                        left: 0 !important;
                        /*background-color: white;*/
                        z-index: 1000;
                    }

                    #previewWrapper .page-footer .footer-content-wrapper {
                        padding-left: ${this.cssPageHorizontalPaddingValue}
                        padding-right: ${this.cssPageHorizontalPaddingValue}
                        padding-bottom: ${this.cssPageVerticalPaddingValue}
                    }

                    #previewWrapper .page-header {
                        /*min-height: 20mm !important; */
                        /*position: sticky !important; */
                        position: sticky !important;
                        top: 0 !important;
                        left: 0 !important;
                    }

                    #previewWrapper tr {
                        height: 6mm !important;
                    }

                    ${["t", "b", "r", "l", "s", "e", "a", "", "x", "y"]
                        .map((side) => {
                            return InvoiceHtmlBuilder.generateGapCSSClasses(
                                [0, 2, 4, 8, 12, 16, 24, 32, 48],
                                "margin",
                                side
                            );
                        })
                        .join("\n")}

                    ${["t", "b", "r", "l", "s", "e", "a", "", "x", "y"]
                        .map((side) => {
                            return InvoiceHtmlBuilder.generateGapCSSClasses(
                                [0, 2, 4, 8, 12, 16, 24, 32, 48],
                                "padding",
                                side
                            );
                        })
                        .join("\n")}

                    #previewWrapper .text-sm-start, #previewWrapper.text-sm-start {
                        text-align: left !important;
                    }

                    #previewWrapper .text-left, #previewWrapper.text-left {
                        text-align: left !important;
                    }

                    #previewWrapper .text-sm-end, #previewWrapper.text-sm-end {
                        text-align: right !important;
                    }

                    #previewWrapper .text-right, #previewWrapper.text-right {
                        text-align: right !important;
                    }

                    #previewWrapper .text-sm-center, #previewWrapper.text-sm-center {
                        text-align: center !important;
                    }

                    #previewWrapper .text-center, #previewWrapper.text-center {
                        text-align: center !important;
                    }

                    #previewWrapper p {
                        margin-bottom: 0 !important;
                    }

                    ${this.customClasses}

                    ${this.mainTableRowStyleCss}

                    ${this.vatTableRowStyleCss}

                    ${
                        !this.forPrint
                            ? `/*.emitter-block:before {
                            content: '';
                            position: absolute;
                            top: 0;
                            left: 0;
                            right: 0;
                            bottom: 0;
                            background: rgba(0, 0, 0, 0.2);
                            z-index: -1;
                        } */
                        .editing {
                            position: relative;
                            outline: 0.1mm solid rgba(242, 177, 42, 1);
                        }
                        .editing:before {
                            content: ''; /* Required for the overlay to work */
                            display: block;
                            position: absolute;
                            width: 100%;
                            height: 100%;
                            top: 0; /* Align to the top */
                            left: 0; /* Align to the left */
                            right: 0; /* Align to the right */
                            bottom: 0; /* Align to the bottom */
                            background: rgba(242, 177, 42, 0.1); /* Darker overlay for the editing state */
                            z-index: 2; /* Ensure the overlay is above the block's content */
                        }`
                            : ""
                    }

                </style>
            </head>
        `;
    }

    static marginsMap = {
        0: "5mm",
        1: "10mm",
        2: "20mm",
        3: "30mm",
        4: "40mm",
    };

    protected get cssPagePaddingValue(): string {
        // const horizontalMargin = Math.min(invoiceThemeSettings?.page?.horizontalMargin == null ? 1 : invoiceThemeSettings?.page?.horizontalMargin, 4)
        // const verticalMargin = Math.min(invoiceThemeSettings?.page?.verticalMargin == null ? 1 : invoiceThemeSettings?.page?.verticalMargin, 4)
        // return this.marginsMap[verticalMargin] + " " + this.marginsMap[horizontalMargin] +";"
        const horizontalMargin = this.invoiceThemeSettings?.page?.horizontalMargin ?? "0";
        const verticalMargin = this.invoiceThemeSettings?.page?.verticalMargin ?? "0";
        return `${verticalMargin}mm ${horizontalMargin}mm !important;`;
    }

    protected get cssPageVerticalPaddingValue(): string {
        // const verticalMargin = Math.min(invoiceThemeSettings?.page?.verticalMargin == null ? 1 : invoiceThemeSettings?.page?.verticalMargin, 4)
        // return this.marginsMap[verticalMargin] + " !important;"
        const verticalMargin = this.invoiceThemeSettings?.page?.verticalMargin ?? "0";
        return `${verticalMargin}mm !important;`;
    }

    protected get extraWidthForBackground() {
        const width = this.invoiceThemeSettings?.page?.horizontalMargin
            ? Number(this.invoiceThemeSettings.page.horizontalMargin)
            : 0;
        return `${2 * width}mm`;
    }

    protected get extraHeightForBackground() {
        const height = this.invoiceThemeSettings?.page?.verticalMargin
            ? Number(this.invoiceThemeSettings.page.verticalMargin)
            : 0;
        return `${2 * height}mm`;
    }

    protected get cssPageHorizontalPaddingValue() {
        // const horizontalMargin = Math.min(invoiceThemeSettings?.page?.horizontalMargin == null ? 1 : invoiceThemeSettings?.page?.horizontalMargin, 4)
        // return this.marginsMap[horizontalMargin] +" !important;"
        const horizontalMargin = this.invoiceThemeSettings?.page?.horizontalMargin ?? "0";
        return `${horizontalMargin}mm !important;`;
    }

    protected get fontFamilyValue() {
        const fontFamily = this.invoiceThemeSettings?.page?.fontFamily;
        // font-family: "Roboto", sans-serif;
        return `"${fontFamily}", sans-serif, "Circular Std Book" !important;`;
    }

    protected fontSizeValue(separator = ";") {
        const fontSize = this.invoiceThemeSettings?.page?.fontSize || 11;
        // font-family: "Roboto", sans-serif;
        return `${fontSize}pt !important ${separator}`;
    }

    protected get logoStyleValue() {
        // style=" width: 100%; max-width: 80px; height: auto;"
        const widthPercent =
            (this.invoiceThemeSettings.headerContentBox?.iconBox?.logoSizeMultiplier || 1) * 100;
        return `width: ${widthPercent}%; max-width: 60mm; height: auto; max-height: 30mm;  object-fit: contain;`;
    }

    static generateGapCSSClasses(pxGaps: any[], type: "padding" | "margin", side: string) {
        // Sides "t" | "b" | "r" | "l" | "s" | "e" | "a" | "" | "x" | "y"
        let cssClasses = [];
        const prefix = type === "margin" ? "m" : "p";

        for (const gap of pxGaps) {
            let content = "";
            const classTitle = `.${prefix}${side}-${gap}px`;
            switch (side) {
                case "t":
                    content = `
                        ${classTitle} {
                            ${type}-top: ${gap}px !important;
                        }
                    `;
                    break;
                case "b":
                    content = `
                        ${classTitle} {
                            ${type}-bottom: ${gap}px !important;
                        }
                    `;
                    break;
                case "r":
                case "e":
                    content = `
                        ${classTitle} {
                            ${type}-right: ${gap}px !important;
                        }
                    `;
                    break;
                case "l":
                case "s":
                    content = `
                        ${classTitle} {
                            ${type}-left: ${gap}px !important;
                        }
                    `;
                    break;
                case "a":
                case "":
                    content = `
                        ${classTitle} {
                            ${type}: ${gap}px !important;
                        }
                    `;
                    break;
                case "x":
                    content = `
                        ${classTitle} {
                            ${type}-left: ${gap}px !important;
                            ${type}-right: ${gap}px !important;
                        }
                    `;
                    break;
                case "y":
                    content = `
                        ${classTitle} {
                            ${type}-top: ${gap}px !important;
                            ${type}-bottom: ${gap}px !important;
                        }
                    `;
                    break;
                default:
                    content = "";
            }
            cssClasses.push(content);
        }

        return cssClasses.join("\n");
    }

    public generateHtml() {
        return this.generateHtmlTemplate();
    }

    public build() {
        return this.generateHtmlTemplate();
    }

    protected get logo() {
        return this.company?.logo?.signedUrl;
    }

    get getInvalidColumns() {
        let invoiceType = "invoice";
        if (this.processedInvoiceData.depositOf) {
            invoiceType = "deposit";
        }
        return InvoiceThemeService.invalidColumsForInvoice({
            invoiceType,
            invoiceData: this.processedInvoiceData,
            invoiceCompany: this.company,
        });
    }

    /**
     * If set, only these columns will be allowed for display. When given an empty list, all columns will be considered
     * @returns null or list of columns
     */
    get allowedColumns() {
        return null;
    }

    // processInvoiceData
    get processedInvoiceData() {
        // TODO check if this line is needeed
        // this.computation = this.invoiceService.computation  // This processes and makes necessary computations in InvoiceService

        const processed = JSON.parse(JSON.stringify(this.invoiceData));
        // Note that netAmountTotal (netAmount), grossAmountTotal (grossAmount) and vatAmountTotal (vatAmount) are returned as negative values in the computation service.
        processed.netAmount = -this.invoiceService.netAmountTotal;
        processed.grossAmount = -this.invoiceService.grossAmountTotal;
        processed.vatAmount = -this.invoiceService.vatAmountTotal;

        if (processed.discount == null) {
            processed.discount = this.invoiceService.discount;
        }
        if (processed.discountPercentage == null) {
            processed.discountPercentage = this.invoiceService.discountPercentage;
        }
        // Payment details is not mandatory make sure, we can set it as empty string
        // if (!processed.paymentDetails && processed.data) {
        //     processed.paymentDetails = processed.data.paymentDetails || "";
        // }
        if (!processed.additionalComment && processed.data) {
            processed.additionalComment = processed.data.additionalComment || "";
        }
        if (processed.date && !moment(processed.date, "DD/MM/YYYY", true).isValid()) {
            processed.date = moment(processed.date).format("DD/MM/YYYY");
        }
        if (processed.dueDate && !moment(processed.dueDate, "DD/MM/YYYY", true).isValid()) {
            processed.dueDate = moment(processed.dueDate).format("DD/MM/YYYY");
        }
        if (processed?.type) {
            processed.type = InvoiceThemeService.getDocumentTypeFriendlyName(processed);
        }
        if (processed?.client?.siret) {
            processed.client.siret = processed.client.siret.split("_")[0];
        }
        if (processed?.company?.siret) {
            processed.company.siret = processed.company.siret.split("_")[0];
        }
        if (processed?.status === "DRAFT") {
            processed.number = "Brouillon";
        }

        processed.lines = this.invoiceService.lines.map((l) => {
            const discountPercentageAmount = (l.netAmount * l.discountPercentage) / 100;
            const obj = {
                ...l,
                discountPercentageAmount,
                discountedNetAmount: l.netAmount - (l.discount ?? discountPercentageAmount ?? 0),
            };
            if (obj.type === "TOTAL") {
                obj.title = "Sous-total";
            }
            return obj;
        });

        // if (processed?.lines) {
        //     processed.linesPages = this.splitTableIntoPages(processed.lines)
        // }

        // console.log("processed", processed)
        return processed;
    }

    // splitTableIntoPages(lines) {
    //     const pages = []

    //     const maxLinesPerPage = 3

    //     for (let i = 0; i < lines.length; i += maxLinesPerPage) {
    //         pages.push(lines.slice(i, i + maxLinesPerPage))
    //     }

    //     return pages
    // }

    static formatDate(date) {
        if (!date) {
            return "";
        }
        if (!moment(date, "DD/MM/YYYY", true).isValid()) {
            return moment(date).format("DD/MM/YYYY");
        }
    }

    // public generateHtmlTemplate() {
    //     console.log("generateHtmlTemplate", this.domInfo)
    //     if (!this.domInfo) {
    //         return this.generateFullSinglePageHtmlTemplate()
    //     }
    //     return `
    //         <!DOCTYPE html>
    //         <html lang="fr">
    //             ${this.buildHead()}
    //             ${this.buildBody()}
    //         </html>
    //     `
    // }

    public generateHtmlTemplate() {
        if (!this.domInfo) {
            return this.generateFullSinglePageHtmlTemplate();
        }
        return `
            <!DOCTYPE html>
            <html lang="fr">
                ${this.buildHead()}
                ${this.buildBody()}
            </html>
        `;
    }

    protected buildBody() {
        return `
            <body id="previewBody">
                <div id="previewWrapper" class="container mx-0px" style="position: relative;">
                    <div class="background-image"></div>
                    <div id="innerWrapper" style="height: 100%; box-sizing: border-box;">
                        <!-- <div class="row g-0 no-gutters px-16px mx-0px" style="box-sizing: border-box; /*height: calc(100vh - 160px);*/ overflow-y: auto; overflow-x: hidden;"> -->

                        ${this.makePages}

                    </div>
                </div>
            </body>
        `;
    }

    public generateFullSinglePageHtmlTemplate() {
        return `
            <!DOCTYPE html>
            <html lang="fr">
                ${this.buildHead()}
                ${this.buildFullSinglePageBody()}
            </html>
        `;
    }

    protected buildFullSinglePageBody() {
        // We use dummy page numbers in buildInvoiceFooter to generate the pagination block
        return `
            <body id="previewBody">
                <div id="previewWrapper" class="container mx-0px" style="position: relative;">
                    <div class="background-image"></div>
                    <div id="innerWrapper" style="height: 100%; box-sizing: border-box;">
                        <!-- <div class="row g-0 no-gutters px-16px mx-0px" style="box-sizing: border-box; /*height: calc(100vh - 160px);*/ overflow-y: auto; overflow-x: hidden;"> -->

                        <div id="fullPage" class="page full">
                            <div class="pageContent row g-0 no-gutters">
                                ${this.buildInvoiceHeader}
                                ${this.buildInvoiceEmitterReceiver()}
                                ${this.buildExtraPagesInvoiceNumberBlock(true)}
                                ${this.buildDocumentAndTitleBlock}
                                ${this.buildInvoiceTableFromLines(this.processedInvoiceData.lines)}
                                ${this.buildDepositsRowsBox}
                                ${this.buildVATAndTotalBox}
                                ${this.buildPaymentModeBlock}
                                ${this.buildPaymentDetailsBlock}
                                ${this.buildInvoiceFooter(1, 2)}
                            </div>
                        </div>

                    </div>
                </div>
            </body>
        `;
    }

    protected newPage(
        contents: any[],
        header: string | boolean = true,
        footer: string | boolean = true,
        pageNumber = null,
        totalPages = null
    ) {
        return `
            <div class="page">
                <div class="pageContent row g-0 no-gutters">
                    ${
                        header && typeof header === "string"
                            ? header
                            : header
                            ? this.buildInvoiceHeader
                            : ""
                    }
                    ${contents.join("\n")}
                    ${
                        footer && typeof footer === "string"
                            ? footer
                            : footer
                            ? this.buildInvoiceFooter(pageNumber, totalPages)
                            : ""
                    }
                </div>
            </div>
        `;
    }

    protected get makePages() {
        if (!this.domInfo) {
            throw Error("domInfo not set. Rendering info is required to use makePages()");
        }
        const startTime = +new Date();
        const maxPageHeight =
            ((this.domInfo.page.offsetWidth * this.scaleSize) / this.pageWidthInMM) *
            this.pageHeightInMM;
        // console.log("maxPageHeight", maxPageHeight, "width", this.domInfo.page.offsetWidth * this.scaleSize)
        // console.log("domInfo", this.domInfo)

        if (maxPageHeight === 0) {
            // debugger
            return "";
        }

        let content = "";

        this.internalNPages = 0;

        const lines = this.processedInvoiceData.lines;
        const rows = this.domInfo.mainTable.tbody.rows;

        let lStart = 0;
        let lEnd = 1;
        // debugger
        const pages = []; // For invoice data lines
        const pagesRows = []; // For dom tr

        let selectedLines = lines.slice(lStart, lEnd);
        let selectedRows = rows.slice(lStart, lEnd);
        let size = this.firstPageOffset + this.computeSubTableHeight([]);
        if (size > maxPageHeight) {
            console.error(
                "Empty page size is bigger than model size. Model may not be fully rendered yet."
            );
            debugger;
            return;
        }

        if (this.firstPageOffset + this.computeSubTableHeight(rows) < maxPageHeight) {
            // Shortcut. If all lines fit in one page, skip other calculations
            pages.push(lines);
            pagesRows.push(rows);
            // console.log("One page")
        } else {
            while (lEnd <= lines.length && lStart < lines.length) {
                selectedLines = lines.slice(lStart, lEnd);
                selectedRows = rows.slice(lStart, lEnd);
                size =
                    (pages.length === 0 ? this.firstPageOffset : this.pageOffset) +
                    this.computeSubTableHeight(selectedRows);
                //+ this.depositsBoxHeight + this.totalsAndVatBoxHeight + this.paymentDetailsBoxHeight
                while (size < maxPageHeight && lEnd <= lines.length) {
                    lEnd++;
                    selectedLines = lines.slice(lStart, lEnd);
                    selectedRows = rows.slice(lStart, lEnd);
                    size =
                        (pages.length === 0 ? this.firstPageOffset : this.pageOffset) +
                        this.computeSubTableHeight(selectedRows);
                    //+ this.extraPagesTitleBoxHeight
                    //+ this.depositsBoxHeight + this.totalsAndVatBoxHeight + this.paymentDetailsBoxHeight
                }

                while (
                    (size =
                        (pages.length === 0 ? this.firstPageOffset : this.pageOffset) +
                        this.computeSubTableHeight(selectedRows)) > maxPageHeight &&
                    lEnd > lStart &&
                    lEnd > 0
                ) {
                    if (lEnd - 1 === lStart) {
                        // If it is the same as start, keep the content
                        break;
                    } else {
                        // We need to pop the last row
                        selectedLines.pop();
                        selectedRows.pop();
                        lEnd--;
                    }
                }

                pages.push(selectedLines);
                pagesRows.push(selectedRows);

                // console.log("Page from dom", pages.length, "maxPageHeight", maxPageHeight, "lines.length", lines.length, "Size ", size, "lStart", lStart, "lEnd", lEnd, "table", this.computeSubTableHeight(selectedRows), "depositsBoxHeight",
                //             this.depositsBoxHeight, "totalsAndVatBoxHeight", this.totalsAndVatBoxHeight,
                //             "paymentDetailsBoxHeight", this.paymentDetailsBoxHeight, "verticalMargin", this.pageVerticalMarginOffset,
                //             "headerBlockHeight", this.headerBlockHeight, "footerBlockHeight", this.footerBlockHeight,
                //             "emitterReceiverBlockHeight", this.emitterReceiverBlockHeight, "invoiceInfoBlockHeight",
                //             this.invoiceInfoBlockHeight)

                if (lStart === lEnd) {
                    // Break possible overflow. The line may be to tall for the page. Thus, it will loop forever
                    // The overflowing line is still added to the page. To remove the overflowing line, the code should come before the content is pushed
                    break;
                }

                lStart = lEnd;
            }
        }

        // const step1EndTime = +new Date()

        // let i = 0
        let extraPages = [];
        let mainPages = [];
        // let i = 0
        for (const _i in pages) {
            const i = +_i;
            const lines = pages[i];
            const rows = pagesRows[i];
            const pageContent = [];

            if (i === 0) {
                pageContent.push(
                    this.buildInvoiceEmitterReceiver(),
                    this.buildDocumentAndTitleBlock
                );
                // content += this.newPage([this.buildInvoiceTableFromLines(lines)])
            } else {
                // pageContent.push(this.buildExtraPagesInvoiceNumberBlock())
                pageContent.push(this.buildExtraPagesRepeatedHeadBlocks);
            }

            pageContent.push(this.buildInvoiceTableFromLines(lines));

            extraPages = [];
            if (i === pages.length - 1) {
                // If last page, check if other content fit within the page. Otherwise, create extra pages

                // if (pages.length > 1) {
                //     console.log("pages", pages.length, pages)
                //     console.log("pagesRows", pagesRows.length, pagesRows)
                //     debugger
                // }

                const lastPageSize =
                    (i === 0 ? this.firstPageOffset : this.pageOffset) +
                    this.computeSubTableHeight(rows);

                if (
                    lastPageSize +
                        this.depositsBoxHeight +
                        this.totalsAndVatBoxHeight +
                        this.paymentDetailsBoxHeight <
                    maxPageHeight
                ) {
                    pageContent.push(
                        this.buildDepositsRowsBox,
                        this.buildVATAndTotalBox,
                        this.buildPaymentModeBlock,
                        this.buildPaymentDetailsBlock
                    );

                    // console.log("pages.length", pages.length, "pagesRows.length", pagesRows.length, "maxPageHeight", maxPageHeight, "i", i, "lastPageSize",
                    //     lastPageSize, "rows.length=", rows.length, "this.computeSubTableHeight(rows) = ", this.computeSubTableHeight(rows), "this.firstPageOffset",
                    //     this.firstPageOffset, "this.pageOffset", this.pageOffset, "this.depositsBoxHeight", this.depositsBoxHeight,
                    //     "this.totalsAndVatBoxHeight", this.totalsAndVatBoxHeight,
                    //     "this.paymentDetailsBoxHeight", this.paymentDetailsBoxHeight, "Last page --------------- ALL IN", "rows", rows)
                } else if (
                    lastPageSize + this.depositsBoxHeight + this.totalsAndVatBoxHeight <
                    maxPageHeight
                ) {
                    pageContent.push(this.buildDepositsRowsBox, this.buildVATAndTotalBox);

                    // extraPages = [this.newPage([this.buildPaymentDetailsBlock()])]
                    extraPages = [
                        [this.buildExtraPagesRepeatedHeadBlocks, this.buildPaymentDetailsBlock],
                    ];

                    // console.log("pages.length", pages.length, "pagesRows.length", pagesRows.length, "Last page --------------- DEPS AND VAT IN,  PAYM OUT")
                } else if (lastPageSize + this.depositsBoxHeight < maxPageHeight) {
                    pageContent.push(this.buildDepositsRowsBox);

                    // extraPages = [this.newPage([this.buildVATAndTotalBox(), this.buildPaymentDetailsBlock()])]
                    extraPages = [
                        [
                            this.buildExtraPagesRepeatedHeadBlocks,
                            this.buildVATAndTotalBox,
                            this.buildPaymentModeBlock,
                            this.buildPaymentDetailsBlock,
                        ],
                    ];

                    // console.log("pages.length", pages.length, "pagesRows.length", pagesRows.length, "Last page --------------- DEPS IN, VAT AND PAYM OUT")
                } else {
                    // extraPages = [
                    //     [this.buildDepositsRowsBox(), this.buildVATAndTotalBox(), this.buildPaymentDetailsBlock()]
                    // ]
                    // console.log("pages.length", pages.length, "pagesRows.length", pagesRows.length, "Last page --------------- NONE IN, ")
                    if (
                        this.pageOffset +
                            this.depositsBoxHeight +
                            this.totalsAndVatBoxHeight +
                            this.paymentDetailsBoxHeight <
                        maxPageHeight
                    ) {
                        extraPages = [
                            [
                                this.buildExtraPagesRepeatedHeadBlocks,
                                this.buildDepositsRowsBox,
                                this.buildVATAndTotalBox,
                                this.buildPaymentModeBlock,
                                this.buildPaymentDetailsBlock,
                            ],
                        ];

                        // console.log("pages.length", pages.length, "pagesRows.length", pagesRows.length, "Last page --------------- NONE IN, DEPS VAT AND PAY ON SAME NEW PAGE")
                    } else if (
                        this.pageOffset + this.depositsBoxHeight + this.totalsAndVatBoxHeight <
                        maxPageHeight
                    ) {
                        extraPages = [
                            [
                                this.buildExtraPagesRepeatedHeadBlocks,
                                this.buildDepositsRowsBox,
                                this.buildVATAndTotalBox,
                            ],
                            [this.buildExtraPagesRepeatedHeadBlocks, this.buildPaymentDetailsBlock], // TODO split paymentDetails into many pages if necessary
                        ];
                        // console.log("pages.length", pages.length, "pagesRows.length", pagesRows.length, "Last page --------------- NONE IN, DEPS AND VAT ON SAME NEW PAGE, PAYM ON NEW PAGE")
                    } else if (this.pageOffset + this.depositsBoxHeight < maxPageHeight) {
                        extraPages = [
                            [this.buildExtraPagesRepeatedHeadBlocks, this.buildDepositsRowsBox],
                            // [this.buildVATAndTotalBox(), this.buildPaymentDetailsBlock()]
                        ];
                        if (
                            this.pageOffset +
                                this.totalsAndVatBoxHeight +
                                this.paymentDetailsBoxHeight <
                            maxPageHeight
                        ) {
                            extraPages.push([
                                this.buildExtraPagesRepeatedHeadBlocks,
                                this.buildVATAndTotalBox,
                                this.buildPaymentModeBlock,
                                this.buildPaymentDetailsBlock,
                            ]);
                            // console.log("pages.length", pages.length, "pagesRows.length", pagesRows.length, "Last page --------------- NONE IN, DEPS ON NEW PAGE, VAT AND PAYM ON SAME NEW PAGE")
                        } else {
                            extraPages.push([
                                this.buildExtraPagesRepeatedHeadBlocks,
                                this.buildVATAndTotalBox,
                            ]);
                            extraPages.push([
                                this.buildExtraPagesRepeatedHeadBlocks,
                                this.buildPaymentModeBlock,
                                this.buildPaymentDetailsBlock,
                            ]);

                            // console.log("pages.length", pages.length, "pagesRows.length", pagesRows.length, "Last page --------------- NONE IN, DEPS ON NEW PAGE, VAT ON NEW PAGE, PAYM ON NEW PAGE")
                            // TODO split paymentDetails into many pages if necessary
                        }
                    } else {
                        debugger;
                    }
                }
                mainPages.push(pageContent);
                // content += this.newPage(pageContent, null, null, i + 1) + extraPages.map((content, index) => this.newPage(content, null, null, (i + 1) + (index + 1))).join("\n")
            } else {
                // content += this.newPage(pageContent, null, null, i + 1)
                mainPages.push(pageContent);
            }

            // i++
        }

        // const step2EndTime = +new Date()

        this.internalNPages = mainPages.length + extraPages.length;
        content = mainPages
            .map((contents, index) =>
                this.newPage(
                    contents,
                    index === 0 || this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.header,
                    index === 0 || this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.footer,
                    index + 1,
                    this.internalNPages
                )
            )
            .join("\n");
        if (extraPages?.length) {
            content += extraPages
                .map((contents, index) =>
                    this.newPage(
                        contents,
                        this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.header,
                        this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.footer,
                        mainPages.length + (index + 1),
                        this.internalNPages
                    )
                )
                .join("\n");
        }

        // const endTime = +new Date()
        // console.log("mainPages", mainPages)
        // console.log("extraPages", extraPages)

        // console.log("Pages computation time", "Step 1 =", step1EndTime - startTime, "Step 2 =", step2EndTime - step1EndTime, "Step 3 =", endTime - step2EndTime, "total time =", endTime - startTime, "startTime", startTime, "endTime", endTime, )

        return content;
    }

    protected get buildInvoiceHeader(): string {
        const logo = this.logo;
        return `
        ${
            this.invoiceThemeSettings.page.showPageHeader
                ? `
            <header id="header" class="col col-12 px-0px page-header" style="margin-bottom: 3mm;">
                <div class="header-content-wrapper">
                    <div class="${this.invoiceHeaderBlockClass}" style="${
                      this.invoiceHeaderBlockStyle
                  }">
                        <div class="${this.invoiceHeaderLogoBlockClass}" style="${
                      this.invoiceHeaderLogoBlockStyle
                  }">
                            ${
                                this.invoiceThemeSettings.style.logoHeader
                                    ? `
                            <div class="${
                                this.invoiceHeaderLogoBlockClass
                            }" style="max-height: 30mm; max-width: 60mm; width: 100%; height: 100%; object-fit: contain; overflow: hidden;">
                                ${
                                    logo
                                        ? `<img src="${logo}" alt="${this.getEmitterInfo.name}" style="${this.logoStyleValue}"/>`
                                        : ""
                                }
                            </div>
                            `
                                    : ""
                            }
                        </div>
                        <div class="s-flex-spacer ${this.interLogoAndTypeSpacerClass}"></div>
                        ${
                            !this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox
                                ?.showInvoiceType
                                ? ""
                                : `<div class="${
                                      this.invoiceHeaderInvoiceTypeBlockClass
                                  }" style="font-size: ${
                                      this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox
                                          ?.useInvoiceNumber
                                          ? "1.5em"
                                          : "2em"
                                  }; font-weight: bold; ${this.invoiceHeaderInvoiceTypeBlockStyle}">
                            ${
                                this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox
                                    ?.useInvoiceNumber
                                    ? this.processedInvoiceData.number ?? ""
                                    : ""
                            }
                            ${
                                !this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox
                                    ?.useInvoiceNumber && this.processedInvoiceData.type
                                    ? this.processedInvoiceData.type ?? ""
                                    : ""
                            }
                        </div>`
                        }
                    </div>
                    ${
                        this.invoiceThemeSettings.style.displayHeaderDivider
                            ? `
                        <hr role="separator" aria-orientation="horizontal" class="my-0px v-divider">
                        `
                            : ""
                    }
                </div>
            </header>
            `
                : ""
        }
        `;
    }

    protected get getEmitterInfo() {
        // Expected output format
        // {
        //     name: "Pixello Agency",
        //     siret: "07955603100015",
        //     address: "91 RUE DU DOCTEUR SUREAU",
        //     postalCode: "93160",
        //     city: "NOISY LE GRAND",
        //     legalForm: "SAS"
        // },
        let info;
        if (this.processedInvoiceData?.company) {
            info = {
                ...this.processedInvoiceData.company,
                // ...(this.processedInvoiceData.sender ?? {})
            };
        } else {
            info = {
                ...this.company,
                // ...(this.processedInvoiceData.sender ?? {})
            };
        }
        if (info.siret) {
            info.siret = info.siret.split("_")[0];
        }
        return info;
    }

    protected get getReceiverInfo() {
        if (
            this.processedInvoiceData.data?.receivers instanceof Array &&
            this.processedInvoiceData.data.receivers.length > 0
        ) {
            return {
                ...this.processedInvoiceData.client,
                ...this.processedInvoiceData.data.receivers[0],
            };
        }
        return {
            ...this.processedInvoiceData.client,
        };
    }

    protected buildInvoiceEmitterReceiver(showEmitter = true, showReceiver = true) {
        return `<div id="emitter-receiver" class="col col-12 px-0px" style="${
            this.generateTopPageStyle
        }">
                    <div class="row w-100 no-gutters d-flex ${
                        this.invoiceThemeSettings.position.emitter.sameLevelAsReceiver
                            ? " flex-row "
                            : " flex-column"
                    }">
                        ${!showEmitter ? "" : this.buildInvoiceEmitter}
                        ${
                            this.invoiceThemeSettings.position.emitter.sameLevelAsReceiver
                                ? ""
                                : `<div class="newLine" style=""></div>`
                        }
                        <div class="s-flex-spacer"></div>
                        ${!showReceiver ? "" : this.buildInvoiceReceiver}
                    </div>
                </div>
        `;
    }

    protected get buildInvoiceEmitter() {
        const logo = this.logo;
        const emitter = this.getEmitterInfo;

        return `<div class="emitter-block ${this.invoiceHeaderEmitterBlockClasses}" style="${
            this.generateEmitterStyle
        } ${this.invoiceHeaderEmitterBlockStyle}">
                    ${
                        this.invoiceThemeSettings.emitter.logo
                            ? `
                        <div class="${sanitizeCssClass(
                            this.emitterLogoClass
                        )}" style="width: 100%;">
                            <div class="${sanitizeCssClass(
                                this.emitterLogoClass
                            )}" style="max-height: 30mm; max-width: 60mm; width: 100%; height: 100%; object-fit: contain; overflow: hidden;">
                                <img src="${sanitize(logo)}" style="${sanitizeInlineStyle(
                                  this.logoStyleValue
                              )}"/>
                            </div>
                        </div>

                        <!-- <div class="col col-9"></div> -->
                        `
                            : ""
                    }

                    <div class="${this.getEmitterVColStyle}" style="font-weight: bold; font-size: ${
            InvoiceHtmlBuilder.emitterReceiverNamesFontEM
        }em;">
                        ${
                            this.invoiceThemeSettings.emitter.name && emitter.name
                                ? emitter.name || ""
                                : ""
                        }
                    </div>
                    <div class="${this.getEmitterVColStyle}">
                        ${
                            this.invoiceThemeSettings.emitter.address && emitter.address
                                ? emitter.address || ""
                                : ""
                        }
                    </div>
                    <div class="${this.getEmitterVColStyle}">
                        ${
                            this.invoiceThemeSettings.emitter.address && emitter.postalCode
                                ? emitter.postalCode || ""
                                : ""
                        }
                        ${
                            this.invoiceThemeSettings.emitter.address && emitter.city
                                ? emitter.city || ""
                                : ""
                        }
                    </div>
                    <div class="${this.getEmitterVColStyle}">
                        ${
                            this.invoiceThemeSettings.emitter.address && emitter.country
                                ? emitter.country || ""
                                : ""
                        }
                    </div>
                    <!-- <div class="${this.getEmitterVColStyle}">
                        ${
                            this.invoiceThemeSettings.emitter.email && emitter.email
                                ? "Email : " + emitter.email
                                : ""
                        }
                    </div>
                    <div class="${this.getEmitterVColStyle}">
                        ${
                            this.invoiceThemeSettings.emitter.phone && emitter.phoneNumber
                                ? "Tél : " + emitter.phoneNumber
                                : ""
                        }
                    </div> -->
                    <div class="${this.getEmitterVColStyle}">
                        ${
                            this.invoiceThemeSettings.emitter.siret && emitter.siret
                                ? "SIRET : " + emitter.siret
                                : ""
                        }
                    </div>
                    <div class="${this.getEmitterVColStyle}">
                        ${
                            this.invoiceThemeSettings.emitter.tva && emitter.vatNumber
                                ? "Numéro de TVA : "
                                : ""
                        }
                        ${
                            this.invoiceThemeSettings.emitter.tva && emitter.vatNumber
                                ? emitter.vatNumber || ""
                                : ""
                        }
                    </div>
                    <div class="${this.getEmitterVColStyle}">
                        ${
                            this.invoiceThemeSettings.emitter.ape && emitter.ape
                                ? emitter.ape || ""
                                : ""
                        }
                    </div>
                </div>`;
    }

    protected get buildInvoiceReceiver() {
        const receiver = this.getReceiverInfo;

        return `<div class="receiver-block ${this.invoiceHeaderReceiverBlockClass}" style="${
            this.generateReceiverStyle
        } ${this.invoiceHeaderReceiverBlockStyle}">
                    <!-- <div class="" style="${this.generateReceiverStyle}"> -->
                        <div class="text-right  ${
                            this.getReceiverVColStyle
                        }" style="font-weight: bold; font-size: ${
            InvoiceHtmlBuilder.emitterReceiverNamesFontEM
        }em;">
                            ${
                                this.invoiceThemeSettings.receiver.name && receiver.name
                                    ? receiver.name || ""
                                    : ""
                            }
                        </div>
                        <div class="text-right  ${this.getReceiverVColStyle}">
                            ${
                                this.invoiceThemeSettings.receiver.address && receiver.address
                                    ? receiver.address || ""
                                    : ""
                            }
                        </div>
                        <div class="${this.getReceiverVColStyle}">
                            ${
                                this.invoiceThemeSettings.receiver.address && receiver.postalCode
                                    ? receiver.postalCode || ""
                                    : ""
                            }
                            ${
                                this.invoiceThemeSettings.receiver.address && receiver.city
                                    ? receiver.city || ""
                                    : ""
                            }
                        </div>
                        <div class="${this.getReceiverVColStyle}">
                            ${
                                this.invoiceThemeSettings.receiver.address && receiver.country
                                    ? receiver.country || ""
                                    : ""
                            }
                        </div>
                        <!-- <div class="text-right ${this.getReceiverVColStyle} ">
                            ${
                                this.invoiceThemeSettings.receiver.email && receiver.email
                                    ? "Email : " + receiver.email
                                    : ""
                            }
                        </div>
                        <div class="text-right ${this.getReceiverVColStyle}">
                            ${
                                this.invoiceThemeSettings.receiver.phone && receiver.phoneNumber
                                    ? "Tél : " + receiver.phoneNumber
                                    : ""
                            }
                        </div> -->
                        <div class="${this.getReceiverVColStyle}">
                            ${
                                this.invoiceThemeSettings.receiver.siret && receiver.siret
                                    ? "SIRET : "
                                    : ""
                            }
                            ${
                                this.invoiceThemeSettings.receiver.siret && receiver.siret
                                    ? receiver.siret || ""
                                    : ""
                            }
                        </div>
                        <div class="${this.getReceiverVColStyle}">
                            ${
                                this.invoiceThemeSettings.receiver.tva && receiver.vatNumber
                                    ? "Numéro de TVA : "
                                    : ""
                            }
                            ${
                                this.invoiceThemeSettings.receiver.tva && receiver.vatNumber
                                    ? receiver.vatNumber || ""
                                    : ""
                            }
                        </div>
                        <div class="text-right  ${this.getReceiverVColStyle}">
                            ${
                                this.invoiceThemeSettings.receiver.ape && receiver.ape
                                    ? receiver.ape || ""
                                    : ""
                            }
                        </div>
                    <!-- </div> -->
                </div>`;
    }

    get invoiceVATData() {
        // Expected return format
        // {rows: [
        //     {
        //         netAmount: 200,
        //         vatRate: "20%",
        //         vatAmount: 40,
        //         grossAmount: 240
        //     },
        //     {
        //         netAmount: 100,
        //         vatRate: "10%",
        //         vatAmount: 10,
        //         grossAmount: 110
        //     },
        // ],
        // netAmount: "300,00 €",
        // vatAmount: "50,00 €",
        // grossAmount: "350,00 €"}

        // Expected input lines
        // [{
        //     title: 'Produit 2',
        //     quantity: 2,
        //     unitPrice: 30,
        //     netAmount: 60,
        //     grossAmount: 72,
        //     vatAmount: 12,
        //     vatRate: 20,
        //     description: "Description du produit 2",
        //     discount: 0,
        //     discountPercentage: 10
        // }]

        const vatData = {
            rows: [],
            netAmount: 0,
            vatAmount: 0,
            grossAmount: 0,
        };

        vatData.rows = this.invoiceService.vatAmountTotals;
        vatData.netAmount = this.invoiceService.netAmountTotal;
        vatData.grossAmount = this.invoiceService.grossAmountTotal;
        vatData.vatAmount = this.invoiceService.vatAmountTotal;

        for (const row of vatData.rows) {
            if (row.vatAmount && row.vatAmount < 0) {
                row.vatAmount *= -1;
            }
            // if (row.netAmount == null && row.vatRate != null && row.vatAmount != null) {
            //     debugger
            //     if (row.vatRate !== 0) {
            //         row.netAmount = (row.vatAmount / row.vatRate) * 100
            //     }
            // }
            if (row.netAmount && row.netAmount < 0) {
                row.netAmount *= -1;
            }
        }

        // for (const line of processedInvoiceData.lines) {
        //     const existingRateIndex = vatData.rows.findIndex((row) => row.vatRate === line.vatRate)
        //     vatData.netAmount += line.netAmount
        //     vatData.vatAmount += line.vatAmount
        //     vatData.grossAmount += line.grossAmount

        //     if (existingRateIndex > -1) {
        //         vatData.rows[existingRateIndex].netAmount += line.netAmount
        //         vatData.rows[existingRateIndex].vatAmount += line.vatAmount
        //         vatData.rows[existingRateIndex].grossAmount += line.grossAmount
        //     } else {
        //         vatData.rows.push({
        //             netAmount: line.netAmount,
        //             vatRate: line.vatRate,
        //             vatAmount: line.vatAmount,
        //             grossAmount: line.grossAmount
        //         })
        //     }
        // }

        return vatData;
    }

    get paymentTerms() {
        return (
            this.processedInvoiceData?.data?.paymentDetails ||
            this.company?.invoiceTextPaymentDetails ||
            ""
        );
    }

    protected get buildInvoiceTable() {
        if (this.processedInvoiceData?.depositOf && !this.quotation) {
            debugger;
            throw Error(
                "invoiceHtmlViewService.ts < buildInvoiceTable : Quotation is required for deposits"
            );
        }

        return `
            <div class="d-flex flex-column px-0">
                <!-- <div class="col col-12" ></div> -->
                ${this.buildDocumentAndTitleBlock}
                ${this.buildInvoiceTableFromLines(this.processedInvoiceData.lines)}
                ${this.buildBlockAfterTable}

            </div>
        `;

        // return `
        //     <div class="d-flex flex-column px-0">
        //         <!-- <div class="col col-12" ></div> -->
        //         ${this.buildDocumentAndTitleBlock()}
        //         <div class="mainTable-block ${this.invoiceMainTableBlockClass}" style="${this.invoiceMainTableBlockStyle}">
        //             <!-- <div class="px-0px pt-8px my-0"> -->
        //                 <table class="mainTable ${this.mainTableClass}" style="${this.generateTableGeneralStyle()}">
        //                     ${this.buildTableTHead()}
        //                     ${this.buildTableTBody()}
        //                     ${this.buildTableTFoot()}
        //                 </table>
        //             <!-- </div> -->
        //         </div>

        //         ${this.buildBlockAfterTable()}

        //     </div>
        // `
    }

    protected buildInvoiceTableFromLines(lines = []) {
        if (this.processedInvoiceData?.depositOf && !this.quotation) {
            debugger;
            throw Error(
                "invoiceHtmlViewService.ts < buildInvoiceTable : Quotation is required for deposits"
            );
        }

        if (!lines?.length) {
            return "";
        }

        return `
            <div id="mainTableBlock" class="mainTable-block ${
                this.invoiceMainTableBlockClass
            }" style="${this.invoiceMainTableBlockStyle}">
                <div class="tableWrapper" style="${this.mainTableWrapperStyle}">
                    <table class="mainTable ${this.mainTableClass}" style="${
            this.generateTableGeneralStyle
        }">
                        ${this.buildTableTHead}
                        ${this.buildTableTBodyFromLines(lines)}
                        ${this.buildTableTFoot}
                    </table>
                </div>
            </div>
        `;
    }

    protected get buildDocumentAndTitleBlock() {
        // invoiceInfoBlock
        return `
            <div id="invoiceInfo" class="${this.invoiceTitleBlockClass}" style="${
            this.invoiceTitleBlockStyle
        }">
                <p style="font-weight: bold; padding-bottom: 3mm; font-size: ${
                    InvoiceHtmlBuilder.emitterReceiverNamesFontEM
                }em;">${this.processedInvoiceData.title}</p>
                <!-- <p></p> -->
                <table class="align-items-center">
                    <tbody>
                        <tr>
                            ${
                                this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox
                                    ?.useInvoiceNumber
                                    ? `
                                <td class="pr-8px" style="text-align: left;">Type : </td>
                                <td class="px-8px" style="text-align: left;">${
                                    this.processedInvoiceData.type ?? "-"
                                }</td>
                                `
                                    : ""
                            }
                            ${
                                !this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox
                                    ?.useInvoiceNumber
                                    ? `
                                <td class="pr-8px" style="text-align: left;">${
                                    this.processedInvoiceData.type || ""
                                } N° : </td>
                                <td class="px-8px" style="text-align: left;">${
                                    this.processedInvoiceData.number ?? "-"
                                }</td>
                                `
                                    : ""
                            }
                        </tr>
                        <tr>
                            <td class="pr-8px" style="text-align: left;">Date :</td>
                            <td class="px-8px" style="text-align: left;">${
                                this.processedInvoiceData.date
                            }</td>
                        </tr>
                        <tr>
                            <td class="pr-8px" style="text-align: left;">Règlement :</td>
                            <td class="px-8px" style="text-align: left;">${
                                this.processedInvoiceData.dueDate
                            }</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        `;
    }

    protected get buildBlockAfterTable() {
        return `
            ${this.buildDepositsRowsBox}
            ${this.buildVATAndTotalBox}
            ${this.buildPaymentModeBlock}
            ${this.buildPaymentDetailsBlock}
        `;
    }

    get showDiscountInfo() {
        return InvoiceThemeService.showDiscountInfo({ invoiceData: this.processedInvoiceData });
    }

    protected get buildTotalsBlock() {
        // const showDiscountInfo = InvoiceThemeService.showDiscountInfo({invoiceData: this.processedInvoiceData})
        return `
            <table class="totalsTable">
                <tbody>
                    <tr >
                        <td class="px-8px" style="text-align: left;">Total HT : </td>
                        <td class="pl-8px" style="text-align: right;">${newCommonFormatCurrency(
                            this.processedInvoiceData.netAmount
                        )}</td>
                    </tr>
                    ${
                        this.invoiceThemeSettings.summary.showVATSummary
                            ? ""
                            : this.buildSummaryTotalVATRows(this.invoiceVATData)
                    }
                    ${
                        this.showDiscountInfo && this.processedInvoiceData.discount
                            ? `<tr>
                        <td class="px-8px" style="text-align: left;">Remise : </td>
                       <td class="pl-8px" style="text-align: right;">${newCommonFormatCurrency(
                           this.processedInvoiceData.discount
                       )}</td>
                    </tr>`
                            : ""
                    }
                    ${
                        this.showDiscountInfo && this.processedInvoiceData.discountPercentage
                            ? `<tr>
                        <td class="px-8px" style="text-align: left;">Remise : </td>
                        <td class="pl-8px" style="text-align: right;">${this.processedInvoiceData.discountPercentage}%</td>
                    </tr>`
                            : ""
                    }

                    <tr>
                        <td class="px-8px" style="text-align: left;"><strong>Total TTC : </strong></td>
                        <td class="pl-8px" style="text-align: right;"><strong>${newCommonFormatCurrency(
                            this.processedInvoiceData.grossAmount
                        )}</strong></td>
                    </tr>
                </tbody>
            </table>
        `;
    }

    get paymentMode(): string {
        return (
            this.company?.invoiceTextPaymentMode ??
            this.processedInvoiceData?.data?.paymentMode ??
            ""
        );
    }

    protected get buildPaymentModeBlock() {
        return `
            <div id="paymentMode">
                ${
                    this.paymentMode
                        ? `<p data-cy="InvoiceHtmlBuilder-paymentMode" style="font-size: 0.8em; white-space: pre-wrap;margin-top: 7mm;">${this.paymentMode}</p>`
                        : ""
                }
                </div>
        `;
    }

    protected get buildPaymentDetailsBlock() {
        return `
            <div id="paymentDetails" class="${this.invoicePaymentDetailsBlockClass}" style="${
            this.invoicePaymentDetailsBlockStyle
        }">
                <p style="font-size: 0.8em; white-space: pre-wrap;">${this.paymentTerms}</p>
                ${
                    this.processedInvoiceData.additionalComment
                        ? `<br><p style="font-size: 0.8em; white-space: pre-wrap;">${this.processedInvoiceData.additionalComment}</p>`
                        : ""
                }
            </div>
        `;
    }

    protected buildSummaryTotalVATRows(invoiceVATTableData) {
        if (
            this.invoiceThemeSettings.summary.showVATSummary ||
            !InvoiceThemeService.showVatInfo({
                invoiceData: this.processedInvoiceData,
                invoiceCompany: this.company,
            })
        ) {
            return "";
        }
        const rows = [];
        for (const rowData of invoiceVATTableData.rows) {
            if (!rowData.vatRate || Number(`${rowData.vatRate}`.replace(/%/g, "").trim()) === 0) {
                continue;
            }
            rows.push(`
                    <tr style="height: 20px;">
                        <td class="px-8px" style="text-align: left;">Total TVA (${
                            rowData.vatRate
                        }%) : </td>
                        <td class="pl-8px" style="text-align: right;">${newCommonFormatCurrency(
                            rowData.vatAmount
                        )}</td>
                    </tr>
                `);
        }
        return rows.join("\n");
    }

    get mainTableColumns() {
        return this.invoiceThemeSettings.tables.header.columns.filter((c) => c.checked);
    }

    public get buildTableTHead() {
        const headerBookletTable = this.mainTableColumns;
        const tds = [];
        let hi = 0;
        const unusedColumns = this.getInvalidColumns;
        const allowedColumns = this.allowedColumns ?? [];

        for (const header of headerBookletTable) {
            // If column is unused or column is not allowed
            if (
                unusedColumns.includes(header.value) ||
                (allowedColumns.length !== 0 && !allowedColumns.includes(header.value))
            ) {
                continue;
            }
            tds.push(`
                <th id="${"header_" + hi++}" style="${this.generateTableHeaderStyle(header)}">
                    ${header.text}
                </th>
            `);
        }
        // return `
        //     <thead style="${this.generateTableHeaderStyle(null)}">
        //         <tr style="${this.generateTableHeaderStyle(null)}">
        //             ${tds.join("\n")}
        //         </tr>
        //     </thead>
        // `
        return `
            <thead>
                <tr>
                    ${tds.join("\n")}
                </tr>
            </thead>
        `;
    }

    protected buildTableTBodyFromLines(lines: any[] = []) {
        const headerBookletTable = this.mainTableColumns;
        const trs = [];
        let ti = 0;
        const unusedColumns = this.getInvalidColumns;
        const allowedColumns = this.allowedColumns ?? [];
        for (const row of lines) {
            const tds = [];
            let hi = 0;
            const lineType = row.type;
            for (const header of headerBookletTable) {
                // If column is unused or column is not allowed
                if (
                    unusedColumns.includes(header.value) ||
                    ((allowedColumns ?? []).length !== 0 && !allowedColumns.includes(header.value))
                ) {
                    continue;
                }
                const headerValue = header.value === "product" ? "title" : header.value;
                let text = this.safeGetFromMap(row, headerValue);
                if (text != null) {
                    // text = header.type === 'amount' ? newCommonFormatCurrency(this.safeGetFromMap(row, headerValue)) : ( header.type === 'percent' ? `${this.formatPercentage(this.safeGetFromMap(row, headerValue))}` : this.safeGetFromMap(row, headerValue) || '')
                    switch (header.type) {
                        case "amount":
                            text = newCommonFormatCurrency(text);
                            break;
                        case "percent":
                            text = `${this.formatPercentage(text)}`;
                            break;
                        case "number":
                            text = `${this.floatingPointToString(parseFloat(text.toString()))}`;
                            break;
                        default:
                            break;
                    }
                } else {
                    text = "-";
                }
                if (
                    lineType === "TOTAL" &&
                    !["product", "title", "netAmount", "grossAmount"].includes(header.value)
                ) {
                    text = "";
                }
                if (header.value === "product" || header.value === "title") {
                    const description = row.description ?? "";
                    tds.push(`
                        <td id="${
                            "row_" + ti + "_cell_" + hi++
                        }" style="${this.generateTableCellStyle(header, false, lineType)}">
                            <span>${text}</span>
                            ${row?.isOptional ? `<span> (Optionnel)</span>` : ""}
                            ${
                                description.length > 0 &&
                                !this.invoiceThemeSettings?.mainTableBox?.rows
                                    ?.hideProductionDescription
                                    ? `<br><p style="font-size: 0.7em; opacity: 0.8;">${description}</p>`
                                    : ""
                            }
                            ${
                                /*this.processedInvoiceData?.depositOf?.grossAmount ? `<br><span style="font-size: 0.7em;">acompte de la facture de ${newCommonFormatCurrency(this.processedInvoiceData.depositOf.grossAmount)}</span>` : ""*/ ""
                            }
                        </td>
                    `);
                } else if (header.value === "discount") {
                    // Discounts are either in amount or in percentage
                    const text = row.discount
                        ? newCommonFormatCurrency(row.discount)
                        : row.discountPercentage
                        ? `${this.formatPercentage(row.discountPercentage)}`
                        : "-";

                    tds.push(`
                        <td id="${
                            "row_" + ti + "_cell_" + hi++
                        }" style="${this.generateTableCellStyle(header, false, lineType)}">
                            ${
                                this.invoiceThemeSettings?.mainTableBox?.rows
                                    ?.showPercentDiscountAmount &&
                                row.discountPercentage &&
                                row.discountPercentageAmount != null
                                    ? `<span>${newCommonFormatCurrency(
                                          row.discountPercentageAmount
                                      )}</span>
                                <br>
                                <span style="font-size: 0.85em; opacity: 0.8;">${text}</span>
                                `
                                    : `<span>${text}</span>`
                            }
                        </td>
                    `);
                } else if (header.value === "netAmount") {
                    tds.push(`
                        <td id="${
                            "row_" + ti + "_cell_" + hi++
                        }" style="${this.generateTableCellStyle(header, false, lineType)}">
                            ${
                                this.invoiceThemeSettings?.mainTableBox?.rows
                                    ?.showDiscountedAmount &&
                                (row.discount || row.discountPercentage)
                                    ? `<span>${newCommonFormatCurrency(
                                          row.discountedNetAmount
                                      )}</span>
                                <br>
                                <span style="${
                                    this.invoiceThemeSettings?.mainTableBox?.rows?.crossNetAmount
                                        ? "text-decoration: line-through "
                                        : ""
                                }; font-size: 0.85em; opacity: 0.8;">${text}</span>
                                `
                                    : `<span>${text}</span>`
                            }
                        </td>
                    `);
                } else {
                    tds.push(`
                        <td id="${
                            "row_" + ti + "_cell_" + hi++
                        }" style="${this.generateTableCellStyle(header, false, lineType)}">
                            ${text}
                        </td>
                    `);
                }
            }
            trs.push(`
                <tr id="${"row_" + ti}">
                    ${tds.join("\n")}
                </tr>
            `);
            ti++;
        }

        return trs.join("\n");
    }

    public get buildTableTBody() {
        return this.buildTableTBodyFromLines(this.processedInvoiceData.lines);
    }

    protected get buildTableTFoot() {
        return `
            <tfoot style="${this.generateTableFooterStyle}">
            </tfoot>
        `;
    }

    protected get buildVATAndTotalBox() {
        return `
            <div id="vatAndTotals" class="px-0px d-flex flex-row align-items-start" style="margin-top: ${
                InvoiceHtmlBuilder.vatAndTotalsBoxTopMarginMm
            }mm;">
                <div id="vatTable" class="${this.invoiceVATTableBlockClass}" style="${
            this.invoiceVATTableBlockStyle
        }">
                    ${this.buildVATTable(this.invoiceVATData)}
                </div>
                <div class="s-flex-spacer"></div>
                <div id="totals" class="totals-block justify-content-end ${
                    this.invoiceTotalsBlockClass
                }" style="${this.invoiceTotalsBlockStyle}">
                    ${this.buildTotalsBlock}
                </div>
            </div>
        `;
    }

    protected buildVATTable(invoiceVATTableData) {
        if (
            !InvoiceThemeService.showVatInfo({
                invoiceData: this.processedInvoiceData,
                invoiceCompany: this.company,
            })
        ) {
            return "";
        }
        return `
            <div class="tableWrapper" style="${this.vatTableWrapperStyle}">
                <table class="vatTable ${this.vatTableClass} ${
            this.invoiceThemeSettings.summary?.showVATSummary ? "d-table" : "d-none"
        }" style="min-width: 80%; ${this.generateTableGeneralStyle}">
                    ${this.buildVATTableTHead}
                    ${this.buildVATTableTBody(invoiceVATTableData)}
                </table>
            </div>
        `;
    }

    protected get buildVATTableTHead() {
        const tds = [];
        let hi = 0;

        for (let header of this.invoiceThemeSettings.summary.vatTable.columns) {
            if (header.checked) {
                tds.push(`
                    <th id="${"vat_header_" + hi++}" style="${this.generateVATTableHeaderStyle(
                    header
                )}">
                        ${header.text}
                    </th>
                `);
            }
        }
        return `
            <thead>
                <tr>
                    ${tds.join("\n")}
                </tr>
            </thead>
        `;
    }

    protected buildVATTableTBody(invoiceVATTableData) {
        const trs = [];
        let ti = 0;
        for (let row of invoiceVATTableData.rows) {
            if (!row.vatRate || Number(`${row.vatRate}`.replace(/%/g, "").trim()) === 0) {
                continue;
            }
            const tds = [];
            let hi = 0;
            // const headerValues = invoiceThemeSettings.summary.vatTable.columns.filter(c => c.checked).map(c => c.value)
            for (const header of this.invoiceThemeSettings.summary.vatTable.columns) {
                if (!header.checked) {
                    continue;
                }
                const headerValue = header.value;
                let text = this.safeGetFromMap(row, headerValue) ?? "";
                if (header.type === "amount") {
                    text = newCommonFormatCurrency(text);
                } else if (header.type === "percent") {
                    text = this.formatPercentage(text);
                } else if (header.type === "number") {
                    text = this.floatingPointToString(parseFloat(text.toString()));
                }
                // text = header.type === 'amount' ? newCommonFormatCurrency(this.safeGetFromMap(row, headerValue)) : this.safeGetFromMap(row, headerValue) || ''
                tds.push(`
                    <td id="${
                        "vat_row_" + ti + "_cell_" + hi++
                    }" style="${this.generateVATTableCellStyle(header)}">
                        ${text}
                    </td>
                `);
            }
            trs.push(`
                <tr id="${"vat_row_" + ti}">
                    ${tds.join("\n")}
                </tr>
            `);
            ti++;
        }

        return trs.join("\n");
    }

    protected get buildDepositsRows() {
        const deposits = this.processedInvoiceData?.deposits || [];
        const rows = [];
        for (const deposit of deposits) {
            // {#deposits}{title} de {grossAmount} le {date}{/deposits}
            rows.push(
                `<div style="width: 100%;">${deposit.title} de ${newCommonFormatCurrency(
                    deposit.grossAmount
                )} le ${InvoiceHtmlBuilder.formatDate(deposit.date)}</div>`
            );
        }
        return rows.join("\n");
    }

    protected get buildDepositsRowsBox() {
        return `
            <div id="deposits" class="${this.invoiceDepositsRowsBlockClass}" style="${this.invoiceDepositsRowsBlockStyle}"> <!-- DEPOSITS START -->
                ${this.buildDepositsRows}
            </div>
        `;
    }

    get legalForm() {
        if (this.company?.invoiceTextLegalNotices) {
            return this.company.invoiceTextLegalNotices;
        }
        return InvoiceThemeService.defaultLegalForm(this.company);
    }

    protected buildInvoiceFooter(currentPage = null, totalPages = null) {
        return `
            <footer id="footer" class="px-0px page-footer" style="">
                <div class="footer-content-wrapper">
                ${this.buildPaginationBlock(currentPage, totalPages)}
                ${
                    this.invoiceThemeSettings.style.displayFooterDivider &&
                    !this.invoiceThemeSettings.footerContentBox.style.hidden
                        ? `<hr role="separator" aria-orientation="horizontal" class="v-divider">`
                        : ""
                }
                    <div class="px-0px ${this.invoiceFooterTextOuterBlockClass}" style="${
            this.invoiceFooterTextOuterBlockStyle
        }">
                        <div class="px-0px d-flex justify-content-center align-items-center ${
                            this.invoiceFooterTextInnerBlockClass
                        }" style="width: 50%; ${this.invoiceFooterTextInnerBlockStyle}">
                            <p style="text-align:center; font-size: 0.8em;" class="mb-0px">${
                                this.legalForm
                            }</p>
                        </div>
                    </div>
                <div>
            </footer>
        `;
    }

    protected buildExtraPagesInvoiceNumberBlock(always = false) {
        if (
            !always &&
            this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox?.useInvoiceNumber
        ) {
            return "";
        }
        return `
            <div id="extraPageTitle" class="px-0px ${this.invoiceNumberBlockClass}" style="${
            this.invoiceNumberBlockStyle
        }">
                ${this.processedInvoiceData.number ?? "-"}
            </div>
        `;
    }

    protected get buildExtraPagesRepeatedHeadBlocks() {
        if (!this.invoiceThemeSettings?.page?.repeatOnFollowingPages) {
            return this.buildExtraPagesInvoiceNumberBlock(false);
        }
        const blocks = [];

        if (
            this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.emitter ||
            this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.receiver
        ) {
            blocks.push(
                this.buildInvoiceEmitterReceiver(
                    this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.emitter,
                    this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.receiver
                )
            );
        }
        if (this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.titleBox) {
            blocks.push(this.buildDocumentAndTitleBlock);
        } else if (this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.invoiceNumberBox) {
            blocks.push(this.buildExtraPagesInvoiceNumberBlock(false));
        }
        return blocks.join("\n");
    }

    protected buildPaginationBlock(currentPage, totalPages) {
        if (
            totalPages &&
            totalPages <= 1 &&
            !this.invoiceThemeSettings?.paginationBox?.alwaysShow
        ) {
            return "";
        }
        return `
            <div id="pagination" class="d-flex flex-row pagination-wrapper ${
                this.invoicePaginationWrapperClass
            }" style="${this.invoicePaginationWrapperStyle}">
                <div class="${this.invoicePaginationBlockClass}" style="${
            this.invoicePaginationBlockStyle
        }">
                    ${currentPage ?? ""} ${totalPages ? "/" : ""} ${totalPages ?? ""}
                </div>
            </div>
        `;
    }

    public formatStyleFromObjTOStr(style: {}) {
        let out = "";
        for (const key in style) {
            const value = style[key];
            if (value == null) {
                continue;
            }
            out += `${key}: ${value}; `;
        }
        return out;
    }

    protected get generateTopPageStyle() {
        if (this.invoiceThemeSettings?.style?.backgroundColor) {
            return this.formatStyleFromObjTOStr({
                // "margin": "20px",
                // "padding-right":"10px",
                "background-color": "#FFFFFF00", //invoiceThemeSettings?.style?.backgroundColor ? invoiceThemeSettings?.style?.backgroundColor : "",
                // "min-height":"30mm"
                "padding-bottom": "5mm",
            });
        }

        return this.formatStyleFromObjTOStr({
            // "margin": "20px",
            // "padding-right":"10px",
            "background-color": "#ffffff00",
            "min-height": "30mm",
        });
    }

    public get generateReceiverStyle() {
        if (this.invoiceThemeSettings?.receiver?.style) {
            return this.formatStyleFromObjTOStr({
                border: this.invoiceThemeSettings?.receiver?.style?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm solid black`
                    : "0px",
                "background-color": this.invoiceThemeSettings?.receiver?.style?.backgroundColor
                    ? this.invoiceThemeSettings?.receiver?.style?.backgroundColor
                    : null,
                "border-color": this.invoiceThemeSettings?.receiver?.style?.borderColor
                    ? this.invoiceThemeSettings?.receiver?.style?.borderColor
                    : null,
                // "font-size":"10px",
                "font-weight": this.invoiceThemeSettings?.receiver?.style?.fontWeight
                    ? this.invoiceThemeSettings?.firstPage?.receiver?.style?.fontWeight
                    : null,
                "font-family": this.invoiceThemeSettings?.receiver?.style?.fontFamily
                    ? this.invoiceThemeSettings?.receiver?.style?.fontFamily
                    : null,
                color:
                    this.invoiceThemeSettings.receiver?.style?.backgroundColor &&
                    !this.invoiceThemeSettings?.useAdvancedStyling
                        ? this.getAutomaticTextColor(
                              this.invoiceThemeSettings.receiver.style.backgroundColor
                          )
                        : null,
                "text-align": "left",
            });
        }
        return "";
    }

    /**
     * Automatically infer a consistent text color with respect to a given (background) color.
     * @param color
     * @returns
     */
    public getAutomaticTextColor(color) {
        //hexadecimal colors
        if (!color) {
            return color;
        }
        if (/^#{0,1}[0-9A-F]{6,8}$/i.test(color)) {
            let i = 0;
            if (color[0] === "#") {
                i++;
            }
            const rgbcolor = {
                r: parseInt(color.substr(i, 2), 16),
                g: parseInt(color.substr(i + 2, 2), 16),
                b: parseInt(color.substr(i + 4, 2), 16),
            };

            const testResult = 0.3 * rgbcolor["r"] + 0.59 * rgbcolor["g"] + 0.11 * rgbcolor["b"];
            if (testResult <= 128) {
                return "white";
            } else {
                return "";
            }
            // const result = ( ( (0.3 * (rgbcolor['r'])) + (0.59 * (rgbcolor['g'])) + (0.11 * (rgbcolor['b'])) ) <= 128) ? '#FFFFFF' : '#000000'
            // return result
        } else {
            // debugger
            return "";
        }
    }

    protected get generateEmitterStyle() {
        if (this.invoiceThemeSettings?.emitter?.style) {
            return this.formatStyleFromObjTOStr({
                border: this.invoiceThemeSettings?.emitter?.style?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm solid black`
                    : "0px",
                "background-color": this.invoiceThemeSettings?.emitter?.style?.backgroundColor
                    ? this.invoiceThemeSettings?.emitter?.style?.backgroundColor
                    : null,
                "border-color": this.invoiceThemeSettings?.emitter?.style?.borderColor
                    ? this.invoiceThemeSettings?.emitter?.style?.borderColor
                    : null,
                // "font-size":"10px",
                "font-weight": this.invoiceThemeSettings?.emitter?.style?.fontWeight
                    ? this.invoiceThemeSettings?.emitter?.style?.fontWeight
                    : null,
                "font-family": this.invoiceThemeSettings?.emitter?.style?.fontFamily
                    ? this.invoiceThemeSettings?.emitter?.style?.fontFamily
                    : null,
                color:
                    this.invoiceThemeSettings?.emitter?.style?.backgroundColor &&
                    !this.invoiceThemeSettings?.useAdvancedStyling
                        ? this.getAutomaticTextColor(
                              this.invoiceThemeSettings.emitter.style.backgroundColor
                          )
                        : null,
                "text-align": "left",
            });
        }
        return "";
    }

    protected get getEmitterVColStyle() {
        if (this.invoiceThemeSettings?.emitter?.style?.textPosition) {
            if (this.invoiceThemeSettings.emitter.style.textPosition === "center") {
                return "pr-8px pl-0px d-flex justify-content-center";
            }
        }
        return "pr-8px pl-0px d-flex justify-content-start";
    }

    protected get emitterLogoClass() {
        if (!this.invoiceThemeSettings?.emitter?.style?.textPosition) {
            return "";
        }
        return ` d-flex justify-content-${this.invoiceThemeSettings.emitter.style.textPosition} `;
    }

    protected get getReceiverVColStyle() {
        if (this.invoiceThemeSettings?.receiver?.style?.textPosition) {
            if (this.invoiceThemeSettings.receiver.style.textPosition === "left") {
                return "pl-8px pr-0px d-flex justify-content-start";
            }
            if (this.invoiceThemeSettings.receiver.style.textPosition === "center") {
                return "pl-8px pr-0px d-flex justify-content-center";
            }
        }
        return "pl-8px pr-0px d-flex justify-content-end";
    }

    protected get generateTableGeneralStyle() {
        if (this.invoiceThemeSettings?.tables) {
            return this.formatStyleFromObjTOStr({
                border: this.invoiceThemeSettings?.tables?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-collapse": "collapse",
                width: "100%",
            });
        }
    }

    protected get mainTableWrapperStyle() {
        if (this.invoiceThemeSettings?.tables) {
            const styleObj = {
                overflow: "hidden",
                border: this.invoiceThemeSettings?.tables?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-style": this.invoiceThemeSettings?.tables?.rows.border ? "solid" : "none",
                // "border-top-width": this.invoiceThemeSettings?.tables?.rows.border ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm` : "0px",
                // "border-left-width": this.invoiceThemeSettings?.tables?.rows.border ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm` : "0px",
                // "border-right-width": this.invoiceThemeSettings?.tables?.rows.border ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm` : "0px",
                // "border-bottom-width": this.invoiceThemeSettings?.tables?.rows.border ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm` : "0px",
                "border-color": this.invoiceThemeSettings?.tables?.rows?.borderColor
                    ? this.invoiceThemeSettings.tables.rows.borderColor
                    : "black",
                "border-radius": this.invoiceThemeSettings?.mainTableBox?.style?.borderRadius
                    ? `${this.invoiceThemeSettings.mainTableBox.style.borderRadius}mm`
                    : null,
            };
            return this.formatStyleFromObjTOStr(styleObj);
        }
    }

    protected get vatTableWrapperStyle() {
        if (this.invoiceThemeSettings?.tables) {
            const styleObj = {
                overflow: "hidden",
                border: this.invoiceThemeSettings?.vatTableBox?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-style": this.invoiceThemeSettings?.vatTableBox?.rows.border
                    ? "solid"
                    : "none",
                // "border-top-width": this.invoiceThemeSettings?.vatTableBox?.rows.border ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm` : "0px",
                // "border-left-width": this.invoiceThemeSettings?.vatTableBox?.rows.border ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm` : "0px",
                // "border-right-width": this.invoiceThemeSettings?.vatTableBox?.rows.border ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm` : "0px",
                // "border-bottom-width": this.invoiceThemeSettings?.vatTableBox?.rows.border ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm` : "0px",
                "border-color": this.invoiceThemeSettings?.vatTableBox?.rows?.borderColor
                    ? this.invoiceThemeSettings.vatTableBox.rows.borderColor
                    : "black",
                "border-radius": this.invoiceThemeSettings?.vatTableBox?.style?.borderRadius
                    ? `${this.invoiceThemeSettings.vatTableBox.style.borderRadius}mm`
                    : null,
            };
            return this.formatStyleFromObjTOStr(styleObj);
        }
    }

    protected generateTableHeaderStyle(row, withBorders = false) {
        // TODO assess switching from this.invoiceThemeSettings?.tables?.rows to this.invoiceThemeSettings?.mainTableBox?.rows
        if (this.invoiceThemeSettings?.tables?.header) {
            const rowPercentWidth = this.formatTableCellWidth(row?.width) ?? "15%";
            const borderStyles = {
                "border-collapse": "collapse",
                "border-style": this.invoiceThemeSettings?.tables?.rows.border ? "solid" : "none",
                "border-top-width": this.invoiceThemeSettings?.tables?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-left-width": this.invoiceThemeSettings?.tables?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-right-width": this.invoiceThemeSettings?.tables?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-bottom-width": this.invoiceThemeSettings?.tables?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-color": this.invoiceThemeSettings?.tables?.rows?.borderColor
                    ? this.invoiceThemeSettings.tables.rows.borderColor
                    : "black",
            };
            return this.formatStyleFromObjTOStr({
                // "color": this.invoiceThemeSettings.tables.header.backgroundColor && !this.invoiceThemeSettings?.useAdvancedStyling ? this.getAutomaticTextColor(this.invoiceThemeSettings.tables.header.backgroundColor) : null,
                color: !this.invoiceThemeSettings?.mainTableBox?.automaticColors
                    ? this.invoiceThemeSettings?.mainTableBox?.header?.color
                    : this.invoiceThemeSettings?.tables?.header?.backgroundColor
                    ? this.getAutomaticTextColor(
                          this.invoiceThemeSettings?.tables?.header?.backgroundColor
                      )
                    : null,
                "text-align": row?.align ? row.align : "start",
                width: rowPercentWidth,
                padding: "2mm",
                "background-color": this.invoiceThemeSettings?.tables?.header?.backgroundColor
                    ? this.invoiceThemeSettings.tables.header.backgroundColor
                    : null,
                "font-weight": this.invoiceThemeSettings?.tables?.header?.fontWeight
                    ? this.invoiceThemeSettings.tables.header.fontWeight
                    : null,
                ...(withBorders ? borderStyles : {}),
            });
        }
    }

    formatTableCellWidth(width) {
        if (width && String(width).trim().endsWith("%")) {
            return width;
        }
        return width ? String(width) + "%" : width;
    }

    protected generateVATTableHeaderStyle(row, withBorders = false) {
        if (this.invoiceThemeSettings?.tables?.header) {
            const borderStyles = {
                "border-collapse": "collapse",
                "border-style": this.invoiceThemeSettings?.vatTableBox?.rows.border
                    ? "solid"
                    : "none",
                "border-top-width": this.invoiceThemeSettings?.vatTableBox?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-left-width": this.invoiceThemeSettings?.vatTableBox?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-right-width": this.invoiceThemeSettings?.vatTableBox?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-bottom-width": this.invoiceThemeSettings?.vatTableBox?.rows.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-color": this.invoiceThemeSettings?.vatTableBox?.rows?.borderColor
                    ? this.invoiceThemeSettings.vatTableBox.rows.borderColor
                    : "black",
            };

            return this.formatStyleFromObjTOStr({
                // "color": this.invoiceThemeSettings.tables.header.backgroundColor && !this.invoiceThemeSettings?.useAdvancedStyling ? this.getAutomaticTextColor(this.invoiceThemeSettings.tables.header.backgroundColor) : null,
                color: !this.invoiceThemeSettings?.vatTableBox?.automaticColors
                    ? this.invoiceThemeSettings?.vatTableBox?.header?.color
                    : this.invoiceThemeSettings?.vatTableBox?.header?.backgroundColor
                    ? this.getAutomaticTextColor(
                          this.invoiceThemeSettings?.vatTableBox?.header?.backgroundColor
                      )
                    : null,
                "text-align": row?.align ? row.align : "start",
                width: this.formatTableCellWidth(row?.width) ?? "33%",
                padding: "2mm",
                "background-color": this.invoiceThemeSettings?.vatTableBox?.header?.backgroundColor
                    ? this.invoiceThemeSettings.vatTableBox.header.backgroundColor
                    : null,
                "font-weight": this.invoiceThemeSettings?.vatTableBox?.header?.fontWeight
                    ? this.invoiceThemeSettings.vatTableBox.header.fontWeight
                    : null,
                ...(withBorders ? borderStyles : {}),
            });
        }
    }

    protected getHeaderValues(headerBookletTable) {
        return headerBookletTable?.map((h) => h.value) || [];
    }

    get mainTableRowStyleCss() {
        console.log("invoiceThemeSettings", this.invoiceThemeSettings.mainTableBox);
        // TODO assess switching from this.invoiceThemeSettings?.tables?.rows to this.invoiceThemeSettings?.mainTableBox?.rows
        return `
            table.mainTable.alternating tr:nth-child(odd) {
                background-color: ${
                    this.invoiceThemeSettings?.mainTableBox?.rows?.secondaryBackgroundColor
                };
                color: ${
                    !this.invoiceThemeSettings?.mainTableBox?.automaticColors
                        ? this.invoiceThemeSettings.mainTableBox.rows.color
                        : this.getAutomaticTextColor(
                              this.invoiceThemeSettings.mainTableBox.rows
                                  .secondaryRowsBackgroundColor
                          )
                };
            }

            table.mainTable.alternating tr:nth-child(even) {
                background-color: ${this.invoiceThemeSettings?.tables?.rows?.backgroundColor};
                color: ${
                    !this.invoiceThemeSettings?.mainTableBox?.automaticColors
                        ? this.invoiceThemeSettings.mainTableBox.rows.color
                        : this.getAutomaticTextColor(
                              this.invoiceThemeSettings.tables.rows.backgroundColor
                          )
                };
            }

            table.mainTable.simple tr {
                background-color: ${this.invoiceThemeSettings?.tables?.rows?.backgroundColor};
                color: ${
                    !this.invoiceThemeSettings?.mainTableBox?.automaticColors
                        ? this.invoiceThemeSettings.mainTableBox.rows.color
                        : this.getAutomaticTextColor(
                              this.invoiceThemeSettings.tables.rows.backgroundColor
                          )
                };
            }

            table.mainTable.default tr {
                background-color: ${this.invoiceThemeSettings?.tables?.rows?.backgroundColor};
                color: ${
                    !this.invoiceThemeSettings?.mainTableBox?.automaticColors
                        ? this.invoiceThemeSettings.mainTableBox.rows.color
                        : this.getAutomaticTextColor(
                              this.invoiceThemeSettings.tables.rows.backgroundColor
                          )
                };
            }
        `;
    }

    get mainTableClass() {
        let klass = this.invoiceThemeSettings?.mainTableBox?.rows?.mode ?? "";
        return klass;
    }

    protected generateTableCellStyle(headerCol, withBorders = false, lineType: string = "") {
        // TODO assess switching from this.invoiceThemeSettings?.tables?.rows to this.invoiceThemeSettings?.mainTableBox?.rows

        if (this.invoiceThemeSettings?.tables?.rows) {
            const borderStyles = {
                "border-collapse": "collapse",
                "border-style": this.invoiceThemeSettings?.tables?.rows.border ? "solid" : "none",
                // "border-bottom-style": "none",
                // "line-height": this.fontSizeValue(), // TODO add to setting
                "border-top-width": this.invoiceThemeSettings?.tables?.rows?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-left-width": this.invoiceThemeSettings?.tables?.rows?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-right-width": this.invoiceThemeSettings?.tables?.rows?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-bottom-width": this.invoiceThemeSettings?.tables?.rows?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-color": this.invoiceThemeSettings?.tables?.rows?.borderColor
                    ? this.invoiceThemeSettings.tables.rows.borderColor
                    : "#efefef",
            };

            return this.formatStyleFromObjTOStr({
                // "color": this.invoiceThemeSettings?.tables?.rows?.backgroundColor ? this.getAutomaticTextColor(this.invoiceThemeSettings.tables.rows.backgroundColor) : null,
                color: !this.invoiceThemeSettings?.mainTableBox?.automaticColors
                    ? this.invoiceThemeSettings?.mainTableBox?.rows?.color
                    : this.invoiceThemeSettings?.tables?.rows?.backgroundColor
                    ? this.getAutomaticTextColor(
                          this.invoiceThemeSettings.tables.rows.backgroundColor
                      )
                    : null,
                // "text-align": row.includes('Produit') ? 'start' : row.includes('120') ? 'end' : 'center',  // ??? //TODO remove. This lines doesn't make sense. Can't check on text value
                "text-align": headerCol?.align ? headerCol.align : "start",
                padding: `${InvoiceHtmlBuilder.tableCellPaddingMm}mm`, //'5px', // TODO add to setting
                // "padding-right":"10px",

                // "background-color": this.invoiceThemeSettings?.tables?.rows?.backgroundColor ? this.invoiceThemeSettings.tables.rows.backgroundColor : "",  // Leave background color handling to the row level
                "font-weight": headerCol?.boldText || lineType === "TOTAL" ? "bold" : null,
                ...(withBorders ? borderStyles : {}),
            });
        }
    }

    get vatTableRowStyleCss() {
        return `
            table.vatTable.alternating tr:nth-child(odd) {
                background-color: ${
                    this.invoiceThemeSettings.vatTableBox.rows.secondaryBackgroundColor
                };
                colors: ${this.getAutomaticTextColor(
                    this.invoiceThemeSettings.vatTableBox.rows.secondaryBackgroundColor
                )};
            }

            table.vatTable.alternating tr:nth-child(even) {
                background-color: ${this.invoiceThemeSettings.vatTableBox.rows.backgroundColor};
                colors: ${this.getAutomaticTextColor(
                    this.invoiceThemeSettings.vatTableBox.rows.backgroundColor
                )};
            }

            table.vatTable.simple tr {
                background-color: ${this.invoiceThemeSettings.vatTableBox.rows.backgroundColor};
                colors: ${this.getAutomaticTextColor(
                    this.invoiceThemeSettings.vatTableBox.rows.backgroundColor
                )};
            }

            table.vatTable.default tr {
                background-color: ${this.invoiceThemeSettings.vatTableBox.rows.backgroundColor};
                colors: ${this.getAutomaticTextColor(
                    this.invoiceThemeSettings.vatTableBox.rows.backgroundColor
                )};
            }


        `;
    }

    get vatTableClass() {
        let klass = this.invoiceThemeSettings?.vatTableBox?.rows?.mode ?? "";
        return klass;
    }

    protected generateVATTableCellStyle(header, withBorders = false) {
        if (this.invoiceThemeSettings?.tables?.rows) {
            const borderStyles = {
                "border-collapse": "collapse",
                "border-style": this.invoiceThemeSettings?.vatTableBox?.rows.border
                    ? "solid"
                    : "none",
                "border-top-width": this.invoiceThemeSettings?.vatTableBox?.rows?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-left-width": this.invoiceThemeSettings?.vatTableBox?.rows?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-right-width": this.invoiceThemeSettings?.vatTableBox?.rows?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-bottom-width": this.invoiceThemeSettings?.vatTableBox?.rows?.border
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : "0px",
                "border-color": this.invoiceThemeSettings?.vatTableBox?.rows?.borderColor
                    ? this.invoiceThemeSettings.vatTableBox.rows.borderColor
                    : "#efefef",
            };
            return this.formatStyleFromObjTOStr({
                color: !this.invoiceThemeSettings?.vatTableBox?.automaticColors
                    ? this.invoiceThemeSettings?.vatTableBox?.rows?.color
                    : this.invoiceThemeSettings?.vatTableBox?.rows?.backgroundColor
                    ? this.getAutomaticTextColor(
                          this.invoiceThemeSettings.vatTableBox.rows.backgroundColor
                      )
                    : null,
                padding: `${InvoiceHtmlBuilder.tableCellPaddingMm}mm`, //'5px', // TODO add to setting
                "text-align": header?.align ? header.align : "start",
                // "padding-right":"10px",
                "line-height": this.fontSizeValue(), // TODO add to setting
                // "border-bottom-style": "none",
                // "background-color": this.invoiceThemeSettings?.tables?.rows?.backgroundColor ? this.invoiceThemeSettings.tables.rows.backgroundColor : "",
                "font-weight": header?.boldText ? "bold" : null,
                ...(withBorders ? borderStyles : {}),
            });
        }
    }

    protected get generateTableFooterStyle() {
        // TODO assess switching from this.invoiceThemeSettings?.tables?.rows to this.invoiceThemeSettings?.mainTableBox?.rows
        if (this.invoiceThemeSettings?.tables?.footer) {
            return this.formatStyleFromObjTOStr({
                "text-align": "end",
                "padding-right": "2mm",
                "border-collapse": "collapse",
                "border-left-style": this.invoiceThemeSettings?.tables?.rows?.border
                    ? "solid"
                    : "none",
                "border-right-style": this.invoiceThemeSettings?.tables?.rows?.border
                    ? "solid"
                    : "none",
                "border-top-style": this.invoiceThemeSettings?.tables?.rows?.border
                    ? "solid"
                    : "none",
                "border-bottom-style": this.invoiceThemeSettings?.tables?.rows?.border
                    ? "solid"
                    : "none",
                "border-top-width": this.invoiceThemeSettings?.tables?.footer?.borderTop
                    ? "2px"
                    : "1px",
                "border-bottom-width": `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`,
                "border-left-width": `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`,
                "border-right-width": `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`,
                "border-top-color":
                    this.invoiceThemeSettings?.tables?.footer?.totalColorSeparator &&
                    this.invoiceThemeSettings?.tables?.footer?.borderTop
                        ? this.invoiceThemeSettings.tables.footer.totalColorSeparator
                        : "#efefef",
                "border-left-color": this.invoiceThemeSettings?.tables?.rows?.borderColor
                    ? this.invoiceThemeSettings.tables.rows.borderColor
                    : "#efefef",
                "border-right-color": this.invoiceThemeSettings?.tables?.rows?.borderColor
                    ? this.invoiceThemeSettings.tables.rows.borderColor
                    : "#efefef",
                "border-bottom-color": this.invoiceThemeSettings?.tables?.rows?.borderColor
                    ? this.invoiceThemeSettings.tables.rows.borderColor
                    : "#efefef",
                "font-weight": this.invoiceThemeSettings?.tables?.footer?.fontWeight
                    ? this.invoiceThemeSettings.tables.footer.fontWeight
                    : null,
                "background-color": this.invoiceThemeSettings?.tables?.footer?.backgroundColor
                    ? this.invoiceThemeSettings.tables.footer.backgroundColor
                    : null,
            });
        }
    }

    public safeGetFromMap(obj: {} = {}, key, altKey = null) {
        if (obj?.[key] != null || (altKey && obj?.[altKey] != null)) {
            let out = obj[key];
            if (out == null && altKey) {
                return obj[altKey];
            }
            return out;
        }
        return "";
    }

    get bootstrapStyles() {
        return `
            #previewWrapper table {
                caption-side: bottom;
                border-collapse: collapse;
                table-layout: fixed;
                width: 100%;
            }

            #previewWrapper caption {
                padding-top: 0.5rem;
                padding-bottom: 0.5rem;
                color: var(--bs-secondary-color);
                text-align: left;
            }

            #previewWrapper th {
                text-align: inherit;
                text-align: -webkit-match-parent;
                max-height: 50mm;
                overflow-y: hidden;
                word-wrap: break-word;
                hyphens: auto;
            }

            #previewWrapper td {
                word-wrap: break-word;
                overflow-x: hidden;
                white-space: nowrap;
                text-wrap: wrap;
                text-overflow: ellipsis;
                hyphens: auto;
            }

            #previewWrapper thead,
            #previewWrapper tbody,
            #previewWrapper tfoot,
            #previewWrapper tr,
            #previewWrapper td,
            #previewWrapper th {
                border-color: inherit;
                border-style: solid;
                border-width: 0;
            }

            #previewWrapper .justify-content-start, #previewWrapper .justify-content-left {
                justify-content: flex-start !important;
            }

            #previewWrapper .justify-content-end,   #previewWrapper .justify-content-right {
                justify-content: flex-end !important;
            }

            #previewWrapper .justify-content-center {
            justify-content: center !important;
            }

            #previewWrapper .justify-content-between {
            justify-content: space-between !important;
            }

            #previewWrapper .justify-content-around {
            justify-content: space-around !important;
            }

            #previewWrapper .justify-content-evenly {
            justify-content: space-evenly !important;
            }

            #previewWrapper .align-items-start, #previewWrapper .align-items-left {
            align-items: flex-start !important;
            }

            #previewWrapper .align-items-end, #previewWrapper .align-items-right {
            align-items: flex-end !important;
            }

            #previewWrapper .align-items-center {
            align-items: center !important;
            }

            #previewWrapper .align-items-baseline {
            align-items: baseline !important;
            }

            #previewWrapper .align-items-stretch {
            align-items: stretch !important;
            }

            #previewWrapper .align-content-start, #previewWrapper .align-content-left {
            align-content: flex-start !important;
            }

            #previewWrapper .align-content-end, #previewWrapper .align-content-right {
            align-content: flex-end !important;
            }

            #previewWrapper .align-content-center {
            align-content: center !important;
            }

            #previewWrapper .align-content-between {
            align-content: space-between !important;
            }

            #previewWrapper .align-content-around {
            align-content: space-around !important;
            }

            #previewWrapper .align-content-stretch {
            align-content: stretch !important;
            }

            #previewWrapper .align-self-auto {
            align-self: auto !important;
            }

            #previewWrapper .align-self-start {
            align-self: flex-start !important;
            }

            #previewWrapper .align-self-end {
            align-self: flex-end !important;
            }

            #previewWrapper .align-self-center {
                align-self: center !important;
            }

            #previewWrapper .align-self-baseline {
                align-self: baseline !important;
            }

            #previewWrapper .align-self-stretch {
                align-self: stretch !important;
            }

            #previewWrapper .order-first {
                order: -1 !important;
            }

            #previewWrapper .order-0 {
                order: 0 !important;
            }

            #previewWrapper .order-1 {
                order: 1 !important;
            }

            #previewWrapper .order-2 {
                order: 2 !important;
            }

            #previewWrapper .order-3 {
                order: 3 !important;
            }

            #previewWrapper .order-4 {
                order: 4 !important;
            }

            #previewWrapper .order-5 {
                order: 5 !important;
            }

            #previewWrapper .order-last {
                order: 6 !important;
            }

            #previewWrapper .container,
            #previewWrapper.container,
                .container-fluid,
                .container-xxl,
                .container-xl,
                .container-lg,
                .container-md,
                .container-sm {
                --bs-gutter-x: 0; /*1.5rem*/
                --bs-gutter-y: 0;
                width: 100%;
                padding-right: calc(var(--bs-gutter-x) * 0.5);
                padding-left: calc(var(--bs-gutter-x) * 0.5);
                margin-right: auto;
                margin-left: auto;

                padding-top: 0;
                padding-bottom: 0;
            }

            #previewWrapper .row {
                /*--bs-gutter-x: 1.5rem;*/
                --bs-gutter-x: 0.005rem; /* Setting the value to zero messes up  with emitter and receiver alignment */
                --bs-gutter-y: 0;
                display: flex;
                flex-wrap: wrap;
                margin-top: calc(-1 * var(--bs-gutter-y));
                margin-right: calc(-0.5 * var(--bs-gutter-x));
                margin-left: calc(-0.5 * var(--bs-gutter-x));
            }
            #previewWrapper .row > * {
                flex-shrink: 0;
                width: 100%;
                max-width: 100%;
                padding-right: calc(var(--bs-gutter-x) * 0.5);
                padding-left: calc(var(--bs-gutter-x) * 0.5);
                margin-top: var(--bs-gutter-y);
            }

            #previewWrapper .col {
                flex: 1 0 0%;
            }

            #previewWrapper .row-cols-auto > * {
                flex: 0 0 auto;
                width: auto;
            }

            #previewWrapper .row-cols-1 > * {
                flex: 0 0 auto;
                width: 100%;
            }

            #previewWrapper .row-cols-2 > * {
                flex: 0 0 auto;
                width: 50%;
            }

            #previewWrapper .row-cols-3 > * {
                flex: 0 0 auto;
                width: 33.33333333%;
            }

            #previewWrapper .row-cols-4 > * {
                flex: 0 0 auto;
                width: 25%;
            }

            #previewWrapper .row-cols-5 > * {
                flex: 0 0 auto;
                width: 20%;
            }

            #previewWrapper .row-cols-6 > * {
                flex: 0 0 auto;
                width: 16.66666667%;
            }

            #previewWrapper .col-auto {
                flex: 0 0 auto;
                width: auto;
            }

            #previewWrapper .col-1 {
                flex: 0 0 auto;
                width: 8.33333333%;
            }

            #previewWrapper .col-2 {
                flex: 0 0 auto;
                width: 16.66666667%;
            }

            #previewWrapper .col-3 {
                flex: 0 0 auto;
                width: 25%;
            }

            #previewWrapper .col-4 {
                flex: 0 0 auto;
                width: 33.33333333%;
            }

            #previewWrapper .col-5 {
                flex: 0 0 auto;
                width: 41.66666667%;
            }

            #previewWrapper .col-6 {
                flex: 0 0 auto;
                width: 50%;
            }

            #previewWrapper .col-7 {
                flex: 0 0 auto;
                width: 58.33333333%;
            }

            #previewWrapper .col-8 {
                flex: 0 0 auto;
                width: 66.66666667%;
            }

            #previewWrapper .col-9 {
                flex: 0 0 auto;
                width: 75%;
            }

            #previewWrapper .col-10 {
                flex: 0 0 auto;
                width: 83.33333333%;
            }

            #previewWrapper .col-11 {
                flex: 0 0 auto;
                width: 91.66666667%;
            }

            #previewWrapper .col-12 {
                flex: 0 0 auto;
                width: 100%;
            }

            #previewWrapper .g-0,
            #previewWrapper .gx-0 {
                --bs-gutter-x: 0;
            }

            #previewWrapper .g-0,
            #previewWrapper .gy-0 {
                --bs-gutter-y: 0;
            }

            #previewWrapper .g-0 .newLine {
                flex-basis: 100%;
                height: 0;
                visibility: hidden;
            }
        `;
    }

    protected get customClasses() {
        return `
            .s-flex-spacer {
                flex-grow: 1;
                flex-shrink: 0;
                flex-basis: 0;
                padding: 0;
                margin: 0;
                display: flex;
            }

            table.totalsTable td {
                line-height: ${this.fontSizeValue()};
                padding: ${InvoiceHtmlBuilder.tableCellPaddingMm}mm; /* TODO add to settings,*/
                border-collapse: collapse;
            }
        `;
    }

    convertUnit(val, from = null, to = null) {
        // TODO move to service and implement unit conversion
        return val;
    }

    protected baseAdvancedStylesGen(stylesSetings, asString: boolean = false, defaults: any = {}) {
        if (!stylesSetings) {
            return asString ? "" : {};
        }
        const styles = {
            overflow: "hidden",
            width: stylesSetings.boxWidth
                ? `${this.convertUnit(stylesSetings.boxWidth)}mm`
                : defaults.boxWidth ?? "45%",
            height: stylesSetings.boxHeight
                ? `${this.convertUnit(stylesSetings.boxHeight)}mm`
                : defaults.boxHeight ?? "auto",
            padding: stylesSetings.padding
                ? `${this.convertUnit(stylesSetings.padding)}mm`
                : defaults.padding ?? "initial",
            "margin-left": stylesSetings.leftMargin
                ? `${this.convertUnit(stylesSetings.leftMargin)}mm`
                : defaults.leftMargin ?? "initial",
            "margin-right": stylesSetings.rightMargin
                ? `${this.convertUnit(stylesSetings.rightMargin)}mm`
                : defaults.rightMargin ?? "initial",
            "margin-top": stylesSetings.topMargin
                ? `${this.convertUnit(stylesSetings.topMargin)}mm`
                : defaults.topMargin ?? "initial",
            "margin-bottom": stylesSetings.bottomMargin
                ? `${this.convertUnit(stylesSetings.bottomMargin)}mm`
                : defaults.bottomMargin ?? "initial",
            "border-style": stylesSetings.border && stylesSetings.borderColor ? "solid" : null, // "solid",
            "border-width":
                stylesSetings.border && stylesSetings.borderColor
                    ? `${InvoiceHtmlBuilder.defaultBorderWidthInMm}mm`
                    : null, // "1px",
            "border-radius": stylesSetings.borderRadius ? `${stylesSetings.borderRadius}mm` : null, // "1px",
            "border-color":
                stylesSetings.border && stylesSetings.borderColor
                    ? stylesSetings.borderColor
                    : defaults.borderColor ?? null, //"#000",
            "background-color": stylesSetings.backgroundColor
                ? stylesSetings.backgroundColor
                : defaults.backgroundColor ?? null,
            color: stylesSetings.color ? stylesSetings.color : defaults.color ?? null,
        };
        return asString ? this.formatStyleFromObjTOStr(styles) : styles;
    }

    /**
     * Check if number locale string formatting is supported
     * @returns true if supported
     */
    protected toLocaleStringSupportsOptions() {
        return !!(typeof Intl === "object" && Intl && typeof Intl.NumberFormat === "function");
    }

    protected floatingPointToString(number) {
        if (typeof number === "number" && this.toLocaleStringSupportsOptions()) {
            const locale =
                !this.localization || this.localization === "auto" ? undefined : this.localization;
            return number.toLocaleString(locale, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
            });
        }
        return number;
    }

    protected formatPercentage(pc: number | string, nullRepr = "-") {
        if (pc == null) {
            return nullRepr;
        }
        return this.floatingPointToString(parseFloat(pc.toString())) + "%";
    }

    protected get invoiceHeaderBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(
                this.invoiceThemeSettings?.headerContentBox?.style,
                false,
                { boxWidth: "100%" }
            ),
        });
    }

    protected get invoiceHeaderBlockClass() {
        let klass = this.invoiceThemeSettings?.headerContentBox?.style?.hidden
            ? " d-none "
            : " d-flex flex-row ";
        klass += this.editingClass === "invoiceHeader-block" ? "editing" : " ";
        return klass;
    }

    protected get invoiceHeaderLogoBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(
                this.invoiceThemeSettings?.headerContentBox?.iconBox?.style,
                false,
                { boxWidth: "auto" }
            ),
        });
    }

    protected get invoiceHeaderLogoBlockClass() {
        let klass = this.invoiceThemeSettings?.headerContentBox?.iconBox?.style?.hidden
            ? " d-none "
            : " d-flex ";
        klass += this.editingClass === "invoiceHeaderLogo-block" ? "editing" : " ";
        if (this.invoiceThemeSettings?.headerContentBox?.iconBox?.iconPosition) {
            klass += ` justify-content-${this.invoiceThemeSettings.headerContentBox.iconBox.iconPosition} `;
            if (this.invoiceThemeSettings.headerContentBox.iconBox.iconPosition === "right") {
                klass += " order-2 ";
            }
        }
        return klass;
    }

    protected get interLogoAndTypeSpacerClass() {
        let klass = "";
        if (this.invoiceThemeSettings?.headerContentBox?.iconBox?.iconPosition === "right") {
            klass += " order-1 ";
        }
        return klass;
    }

    protected get invoiceHeaderInvoiceTypeBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(
                this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox?.style,
                false,
                { boxWidth: "auto" }
            ),
        });
    }

    protected get invoiceHeaderInvoiceTypeBlockClass() {
        let klass = this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox?.style?.hidden
            ? " d-none "
            : " d-flex align-items-center ";
        klass += this.editingClass === "invoiceHeaderType-block" ? " editing " : " ";
        if (this.invoiceThemeSettings?.headerContentBox?.iconBox?.iconPosition === "right") {
            klass += " order-0 justify-content-left ";
        } else {
            klass += " justify-content-end ";
        }
        return klass;
    }

    protected get invoiceHeaderEmitterBlockStyle() {
        const defaultBoxWidth = this.invoiceThemeSettings.position.emitter.sameLevelAsReceiver
            ? "45%"
            : "auto";
        const styles = {
            // overflow: "hidden",
            // width: this.invoiceThemeSettings?.emitter?.style?.boxWidth ? `${this.convertUnit(this.invoiceThemeSettings.emitter.style.boxWidth)}mm` : "45%",
            // height: this.invoiceThemeSettings?.emitter?.style?.boxHeight ? `${this.convertUnit(this.invoiceThemeSettings.emitter.style.boxHeight)}mm` : "auto",
            // padding: this.invoiceThemeSettings?.emitter?.style?.padding ? `${this.convertUnit(this.invoiceThemeSettings.emitter.style.padding)}mm` : "initial",
            // 'margin-left': this.invoiceThemeSettings?.emitter?.style?.leftMargin ? `${this.convertUnit(this.invoiceThemeSettings.emitter.style.leftMargin)}mm` : "initial",
            // 'margin-right': this.invoiceThemeSettings?.emitter?.style?.rightMargin ? `${this.convertUnit(this.invoiceThemeSettings.emitter.style.rightMargin)}mm` : "initial",
            // 'margin-top': this.invoiceThemeSettings?.emitter?.style?.topMargin ? `${this.convertUnit(this.invoiceThemeSettings.emitter.style.topMargin)}mm` : "initial",
            // 'margin-bottom': this.invoiceThemeSettings?.emitter?.style?.bottomMargin ? `${this.convertUnit(this.invoiceThemeSettings.emitter.style.bottomMargin)}mm` : "initial",

            ...this.baseAdvancedStylesGen(this.invoiceThemeSettings?.emitter?.style, false, {
                boxWidth: defaultBoxWidth,
            }),
        };
        return this.formatStyleFromObjTOStr(styles);
    }

    protected get invoiceHeaderEmitterBlockClasses() {
        // TODO handle logo block classes here
        let klass = this.invoiceThemeSettings?.emitter?.style?.hidden
            ? " d-none "
            : " d-flex flex-column ";
        klass += this.editingClass === "emitter-block" ? "editing" : " ";
        return klass;
    }

    protected get invoiceHeaderReceiverBlockStyle() {
        const defaultBoxWidth = this.invoiceThemeSettings.position.emitter.sameLevelAsReceiver
            ? "45%"
            : "auto";
        const styles = {
            // overflow: "hidden",
            // width: this.invoiceThemeSettings?.receiver?.style?.boxWidth ? `${this.convertUnit(this.invoiceThemeSettings.receiver.style.boxWidth)}mm` : "45%",
            // height: this.invoiceThemeSettings?.receiver?.style?.boxHeight ? `${this.convertUnit(this.invoiceThemeSettings.receiver.style.boxHeight)}mm` : "auto",
            // padding: this.invoiceThemeSettings?.receiver?.style?.padding ? `${this.convertUnit(this.invoiceThemeSettings.receiver.style.padding)}mm` : "initial",
            // 'margin-left': this.invoiceThemeSettings?.receiver?.style?.leftMargin ? `${this.convertUnit(this.invoiceThemeSettings.receiver.style.leftMargin)}mm` : "initial",
            // 'margin-right': this.invoiceThemeSettings?.receiver?.style?.rightMargin ? `${this.convertUnit(this.invoiceThemeSettings.receiver.style.rightMargin)}mm` : "initial",
            // 'margin-top': this.invoiceThemeSettings?.receiver?.style?.topMargin ? `${this.convertUnit(this.invoiceThemeSettings.receiver.style.topMargin)}mm` : "initial",
            // 'margin-bottom': this.invoiceThemeSettings?.receiver?.style?.bottomMargin ? `${this.convertUnit(this.invoiceThemeSettings.receiver.style.bottomMargin)}mm` : "initial",
            ...this.baseAdvancedStylesGen(this.invoiceThemeSettings?.receiver?.style, false, {
                boxWidth: defaultBoxWidth,
            }),
        };
        return this.formatStyleFromObjTOStr(styles);
    }

    protected get invoiceHeaderReceiverBlockClass() {
        // TODO handle logo block classes here
        let klass = this.invoiceThemeSettings?.receiver?.style?.hidden
            ? " d-none "
            : " d-flex flex-column ";
        klass += this.editingClass === "receiver-block" ? "editing" : " ";
        return klass;
    }

    protected get invoiceTitleBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(this.invoiceThemeSettings?.titleBox?.style, false, {
                boxWidth: "60%",
            }),
            "max-height": "100mm",
        });
    }

    protected get invoiceTitleBlockClass() {
        let klass = this.invoiceThemeSettings?.titleBox?.style?.hidden
            ? " d-none "
            : " d-flex flex-column align-items-start ";
        klass += this.editingClass === "invoiceTitle-block" ? "editing" : " ";
        return klass;
    }

    protected get invoiceMainTableBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(this.invoiceThemeSettings?.mainTableBox?.style, false, {
                boxWidth: "100%",
            }),
            "border-radius": null, // Override borderRadius
        });
    }

    protected get invoiceMainTableBlockClass() {
        let klass = this.invoiceThemeSettings?.mainTableBox?.style?.hidden
            ? " d-none "
            : " d-flex ";
        klass += this.editingClass === "mainTable-block" ? "editing" : " ";
        klass += " justify-content-start align-items-start";
        return klass;
    }

    protected get invoiceDepositsRowsBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(this.invoiceThemeSettings?.depositsBox?.style, false, {
                boxWidth: "100%",
                topMargin: "5mm",
            }),
        });
    }

    protected get invoiceDepositsRowsBlockClass() {
        // TODO handle logo block classes here
        let klass = this.invoiceThemeSettings?.depositsBox?.style?.hidden
            ? " d-none "
            : " d-flex flex-column ";
        klass += this.editingClass === "deposits-block" ? "editing" : " ";
        klass += " justify-content-start align-items-start";
        return klass;
    }

    protected get invoiceVATTableBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(this.invoiceThemeSettings?.vatTableBox?.style, false, {
                boxWidth: "45%",
            }),
            "border-radius": null, // Override borderRadius
        });
    }

    protected get invoiceVATTableBlockClass() {
        let klass = this.invoiceThemeSettings?.vatTableBox?.style?.hidden ? " d-none " : " d-flex ";
        klass += this.editingClass === "vatTable-block" ? "editing" : " ";
        klass += " justify-content-start";
        return klass;
    }

    protected get invoiceTotalsBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(this.invoiceThemeSettings?.totalsBox?.style, false, {
                boxWidth: "45%",
            }),
        });
    }

    protected get invoiceTotalsBlockClass() {
        let klass = this.invoiceThemeSettings?.totalsBox?.style?.hidden ? " d-none " : " d-flex ";
        klass += this.editingClass === "totals-block" ? "editing" : " ";
        klass += " justify-content-end";
        return klass;
    }

    protected get invoicePaymentDetailsBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(
                this.invoiceThemeSettings?.paymentDetailsBox?.style,
                false,
                { boxWidth: "100%", topMargin: "5mm" }
            ),
        });
    }

    protected get invoicePaymentDetailsBlockClass() {
        let klass = this.invoiceThemeSettings?.paymentDetailsBox?.style?.hidden
            ? " d-none "
            : " d-flex flex-column ";
        klass += this.editingClass === "invoicePaymentDetails-block" ? "editing" : " ";
        klass += " justify-content-center ";
        return klass;
    }

    protected get invoiceFooterTextOuterBlockStyle() {
        return "";
    }

    protected get invoiceFooterTextOuterBlockClass() {
        let klass = this.invoiceThemeSettings?.footerContentBox?.style?.hidden
            ? " d-none "
            : " d-flex flex-row ";
        klass += this.editingClass === "invoiceFooterTextOuter-block" ? "editing" : " ";
        return klass;
    }

    protected get invoiceFooterTextInnerBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(
                this.invoiceThemeSettings?.footerContentBox?.style,
                false,
                { boxWidth: "100%" }
            ),
        });
    }

    protected get invoiceFooterTextInnerBlockClass() {
        // TODO
        // let klass = this.invoiceThemeSettings?.footerContentBox?.style?.hidden ? ' d-none ' : ' d-flex '
        // klass += this.editingClass === "invoiceFooterTextInner-block" ? 'editing' : ' '
        let klass = this.editingClass === "invoiceFooterTextInner-block" ? "editing" : " ";
        return klass;
    }

    protected get invoiceNumberBlockStyle() {
        return this.formatStyleFromObjTOStr({
            "font-weight": "bold",
            "margin-bottom": "5mm",
        });
    }

    protected get invoiceNumberBlockClass() {
        return "";
    }

    protected get invoicePaginationBlockStyle() {
        return this.formatStyleFromObjTOStr({
            ...this.baseAdvancedStylesGen(this.invoiceThemeSettings?.paginationBox?.style, false, {
                boxWidth: "auto",
                padding: "2mm",
            }),
        });
    }

    protected get invoicePaginationBlockClass() {
        let klass = this.invoiceThemeSettings?.paginationBox?.style?.hidden
            ? " d-none "
            : " d-flex flex-row ";
        klass += this.editingClass === "pagination-block" ? "editing " : " ";
        return klass;
    }

    protected get invoicePaginationWrapperStyle() {
        return "";
    }

    protected get invoicePaginationWrapperClass() {
        let klass = this.invoiceThemeSettings?.paginationBox?.style?.hidden
            ? " d-none "
            : " d-flex flex-row ";
        klass += " justify-content-" + (this.invoiceThemeSettings?.paginationBox?.position ?? "");
        return klass;
    }

    protected get pageVerticalMarginOffset() {
        return (
            (this.domInfo?.page?.verticalPadding ? this.domInfo.page.verticalPadding * 2 : 0) *
            this.scaleSize
        );
    }

    protected get pageHorizontalMarginOffset() {
        return (
            (this.domInfo?.page?.horizontalPadding ? this.domInfo.page.horizontalPadding * 2 : 0) *
            this.scaleSize
        );
    }

    protected get headerBlockHeight() {
        // Block plus margin
        return (
            (this.domInfo?.header?.fullSpaceHeight ? this.domInfo.header.fullSpaceHeight : 0) *
            this.scaleSize
        );
    }

    protected get footerBlockHeight() {
        // Block plus margin
        return (
            (this.domInfo?.footer?.fullSpaceHeight ? this.domInfo.footer.fullSpaceHeight : 0) *
            this.scaleSize
        );
    }

    protected get emitterBlockHeight() {
        // TODO add emitter and receiver separate to domInfo
        return (
            (this.domInfo?.emitterReceiver?.fullSpaceHeight
                ? this.domInfo.emitterReceiver.fullSpaceHeight
                : 0) * this.scaleSize
        );
    }

    protected get receiverBlockHeight() {
        // TODO add emitter and receiver separate to domInfo
        return (
            (this.domInfo?.emitterReceiver?.fullSpaceHeight
                ? this.domInfo.emitterReceiver.fullSpaceHeight
                : 0) * this.scaleSize
        );
    }

    protected get emitterReceiverBlockHeight() {
        return (
            (this.domInfo?.emitterReceiver?.fullSpaceHeight
                ? this.domInfo.emitterReceiver.fullSpaceHeight
                : 0) * this.scaleSize
        );
    }

    protected get invoiceInfoBlockHeight() {
        return (
            (this.domInfo?.invoiceInfo?.fullSpaceHeight
                ? this.domInfo.invoiceInfo.fullSpaceHeight
                : 0) * this.scaleSize
        );
    }

    protected get depositsBoxHeight() {
        return (
            (this.domInfo?.deposits?.fullSpaceHeight ? this.domInfo.deposits.fullSpaceHeight : 0) *
            this.scaleSize
        );
    }

    protected get totalsBlockHeight() {
        return (
            (this.domInfo?.totals?.fullSpaceHeight ? this.domInfo.totals.fullSpaceHeight : 0) *
            this.scaleSize
        );
    }

    protected get vatTableBoxHeight() {
        return (
            (this.domInfo?.vatTable?.fullSpaceHeight ? this.domInfo.vatTable.fullSpaceHeight : 0) *
            this.scaleSize
        );
    }

    protected get totalsAndVatBoxHeight() {
        return (
            (this.domInfo?.vatAndTotals?.fullSpaceHeight
                ? this.domInfo.vatAndTotals.fullSpaceHeight
                : 0) * this.scaleSize
        );
    }

    protected get paymentDetailsBoxHeight() {
        return (
            (this.domInfo?.paymentDetails?.fullSpaceHeight
                ? this.domInfo.paymentDetails.fullSpaceHeight
                : 0) * this.scaleSize
        );
    }

    protected get paginationBoxHeight() {
        return (
            (this.domInfo?.pagination?.fullSpaceHeight
                ? this.domInfo.pagination.fullSpaceHeight
                : 0) * this.scaleSize
        );
    }

    protected get extraPagesTitleBoxHeight() {
        return (
            (this.domInfo?.extraPageTitle?.fullSpaceHeight
                ? this.domInfo.extraPageTitle.fullSpaceHeight
                : 0) * this.scaleSize
        );
    }

    protected get mainTableHeaderHeight() {
        return (
            (this.domInfo?.mainTable?.thead?.fullSpaceHeight
                ? this.domInfo.mainTable.thead.fullSpaceHeight
                : 0) * this.scaleSize
        );
    }

    protected computeSubTableHeight(rows) {
        // Rows are from this.domInfo.mainTable.tbody.rows
        if (!rows?.length) {
            return this.mainTableHeaderHeight;
        }
        return (
            rows.reduce((acc, row) => acc + row.fullSpaceHeight, 0) * this.scaleSize +
            this.mainTableHeaderHeight
        );
    }

    protected get firstPageOffset() {
        // pageVerticalMarginOffset / 2 since the bottom margin is already counted in the footer as bottom padding
        return (
            this.pageVerticalMarginOffset / 2 +
            this.headerBlockHeight +
            this.footerBlockHeight +
            this.emitterReceiverBlockHeight +
            this.invoiceInfoBlockHeight
        ); // Note that pagination is already in the footer
    }

    protected get pageOffset() {
        let size = this.pageVerticalMarginOffset + this.repeatedBlocksHeight;
        if (this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.header) {
            size += this.headerBlockHeight;
        }
        if (this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.footer) {
            size += this.footerBlockHeight - this.pageVerticalMarginOffset / 2; // Remove excess bottom margin .Note that pagination is already in the footer
        }
        return size;
    }

    protected get repeatedBlocksHeight() {
        if (!this.invoiceThemeSettings?.page?.repeatOnFollowingPages) {
            return this.extraPagesTitleBoxHeight;
        }
        let size = 0;

        if (
            this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.emitter ||
            this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.receiver
        ) {
            size += this.emitterReceiverBlockHeight;
        }
        if (this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.titleBox) {
            size += this.invoiceInfoBlockHeight;
        } else if (
            this.invoiceThemeSettings?.page?.repeatOnFollowingPages?.invoiceNumberBox &&
            !this.invoiceThemeSettings?.headerContentBox?.invoiceTypeBox?.useInvoiceNumber
        ) {
            size += this.extraPagesTitleBoxHeight;
        }
        return size;
    }
}
